import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';

// Services
import { userService } from '../../services/users';
import { playlistService } from '../../services/playlists';
import { libraryService } from '../../services/library';
import { favoriteWorksService } from '../../services/favoriteWorks';
import { favoriteActionEventsService } from '../../services/favoriteActionEvents';
import { favoriteRecordingsService } from '../../services/favoriteRecordings';
import { favoriteAlbumsService } from '../../services/favoriteAlbums';
import { favoriteComposerService } from '../../services/favoriteComposers';
import { favoriteArtistService } from '../../services/favoriteArtists';
import { favoritePlaylistsService } from '../../services/favoritePlaylists';
import { worksService } from '../../services/works';
import { libraryAlbumsService, type FetchAlbumsPageResponse } from '../../services/libraryAlbums';
import { librarySyncSettingsService } from '../../services/librarySyncSettings';
import { libraryImportService, type ImportAlbumPayload, type ImportArtistPayload, type ImportComposerPayload, type ImportPlaylistPayload, type ImportTrackPayload } from '../../services/libraryImport';
import { libraryCache, type LibraryLists } from '../../lib/cache/libraryCache';
import { recordingsService } from '../../services/recordings';
import { albumsService } from '../../services/albums';
import { logAppEvent } from '../../services/appEvents';
import { uiActions } from './ui';
import { albumActions } from './album';
import type { FavoriteRecording } from '../../services/favoriteRecordings';

// Interfaces
import type { AppDispatch, RootState } from '../store';
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';
import { LIKED_SONGS_IMAGE, PLAYLIST_DEFAULT_IMAGE } from '../../constants/spotify';
import type { LibraryRecording } from '../../interfaces/recording';
import type { LibraryWork } from '../../interfaces/work';
import type { LibraryComposer } from '../../interfaces/composer';
import { resolveTrackRecordingId } from '../../utils/resolveRecordingId';
import {
  createDefaultLibrarySyncSettings,
  type LibrarySyncAutomationId,
  type LibrarySyncSettingsUpdate,
  type LibrarySyncSettingsValue,
  type SavedAlbumTracksSyncResult,
  type SavedAlbumRecordingsSyncResult,
  type RecordingWorksSyncResult,
  type RecordingsAndWorksSyncJobStatus,
} from '../../interfaces/librarySyncSettings';
import { getFromLocalStorageWithExpiry } from '../../utils/localstorage';
import {
  getCatalogIdForSpotifyId,
  getKnownComposerIds,
  loadCatalogComposerIds,
} from '../../utils/composerRegistry';
import { backendClient } from '../../lib/api/backendClient';
import { isDevUser } from '../../lib/config/devUsers';

const LIB_LOG_ENABLED = true;
const LIB_LOG_PREFIX = '[LibraryImport]';
const libLog = (...args: any[]) => {
  if (!LIB_LOG_ENABLED) return;
  // eslint-disable-next-line no-console
  console.info(LIB_LOG_PREFIX, ...args);
};

const resolveEnvBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return undefined;
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  }
  return undefined;
};

const resolveRuntimeEnvValue = (key: string): unknown => {
  if (typeof window !== 'undefined' && window && typeof (window as any).__ENV === 'object') {
    const env = (window as any).__ENV;
    if (key in env) {
      return env[key];
    }
  }
  if (typeof process !== 'undefined' && process && typeof process.env === 'object') {
    return (process.env as Record<string, string | undefined>)[key];
  }
  return undefined;
};

const rawLegacyComposerFlag = resolveRuntimeEnvValue('REACT_APP_USE_LEGACY_COMPOSER_FAVORITES');
export const LEGACY_COMPOSER_SYNC_ENABLED =
  resolveEnvBoolean(rawLegacyComposerFlag) ?? false;

const rawDisableHeavyFeatures = resolveRuntimeEnvValue('REACT_APP_DISABLE_HEAVY_FEATURES');
const DISABLE_HEAVY_FEATURES = resolveEnvBoolean(rawDisableHeavyFeatures) === true;

// Kill switch to stop legacy library fetch effects (albums/artists/playlists/works/recordings)
// now that import + cache hydration exist. Set to false to re-enable.
export const DISABLE_LEGACY_LIBRARY_FETCHES = true;

const rawDisableAlbumPageFetch = resolveRuntimeEnvValue('REACT_APP_DISABLE_ALBUM_PAGE_FETCH');
// Explicit album flag overrides; otherwise inherit the heavy-features kill-switch.
const resolvedAlbumDisable = resolveEnvBoolean(rawDisableAlbumPageFetch);
const DISABLE_ALBUM_PAGE_FETCH =
  resolvedAlbumDisable !== undefined ? resolvedAlbumDisable : DISABLE_HEAVY_FEATURES;

const LIBRARY_FILTERS = [
  'ALL',
  'ALBUMS',
  'ARTISTS',
  'PLAYLISTS',
  'COMPOSERS',
  'WORKS',
  'RECORDINGS',
] as const;

export type LibraryFilter = (typeof LIBRARY_FILTERS)[number];

export const MIX_BUILDER_ROUTE = '/mix-builder';
export const MIX_BUILDER_PLAYLIST_ID = 'custom-mix-builder';
export const MIX_BUILDER_URI = 'spotify:app:custom-mix-builder';

export const normalizeLibraryFilter = (value: string): LibraryFilter => {
  const upperCased = value.toUpperCase();
  return LIBRARY_FILTERS.includes(upperCased as LibraryFilter)
    ? (upperCased as LibraryFilter)
    : 'ALL';
};

type LibraryItem = Album | Artist | Playlist | LibraryComposer | LibraryWork | LibraryRecording;

type LibrarySyncSettingsStatus = 'idle' | 'loading' | 'ready' | 'error';
type LibrarySyncSettingsUpdateStatus = 'idle' | 'pending';

export interface LibrarySyncSettingsState {
  status: LibrarySyncSettingsStatus;
  updateStatus: LibrarySyncSettingsUpdateStatus;
  value: LibrarySyncSettingsValue;
  error: string | null;
  lastFetchedAt: number | null;
  pendingUpdate: {
    ruleId: LibrarySyncAutomationId;
    previousValue: LibrarySyncSettingsValue;
  } | null;
  isSyncingAlbumTracks: boolean;
  isSyncingAlbumRecordings: boolean;
  isSyncingRecordingWorks: boolean;
  isSyncingAlbumRecordingsAndWorks: boolean;
  isSyncingTrackRecordings?: boolean;
  recordingsAndWorksJob: RecordingsAndWorksSyncJobStatus | null;
  recordingsAndWorksJobId: string | null;
}

const cloneLibrarySyncSettingsValue = (
  value: LibrarySyncSettingsValue
): LibrarySyncSettingsValue => JSON.parse(JSON.stringify(value));

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  if (typeof (error as any)?.message === 'string' && (error as any).message.trim().length > 0) {
    return (error as any).message.trim();
  }

  const responseData = (error as any)?.response?.data;
  if (typeof responseData === 'string' && responseData.trim().length > 0) {
    return responseData.trim();
  }
  if (responseData && typeof responseData === 'object') {
    const messageValue =
      (responseData.message && typeof responseData.message === 'string' && responseData.message) ||
      (responseData.error && typeof responseData.error === 'string' && responseData.error) ||
      (responseData.detail && typeof responseData.detail === 'string' && responseData.detail);
    if (typeof messageValue === 'string' && messageValue.trim().length > 0) {
      return messageValue.trim();
    }
  }

  return fallback;
};

const notifyError = (content: string) => {
  if (typeof window === 'undefined') return;
  void message.error(content);
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const TRACK_BATCH_SIZE = 50;
const TRACK_SAVE_BATCH_DELAY_MS = 500;
const RECORDING_FETCH_BATCH_SIZE = 2000;
// Reduced from 10 to 6 to leave browser connections available for user-initiated navigation requests
// Browsers typically allow 6-10 concurrent connections per domain
const MAX_CONCURRENT_RECORDING_BATCHES = 6;

const collectAlbumTrackIds = async (albumId: string): Promise<string[]> => {
  const uniqueIds = new Set<string>();
  const limit = 50;
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const response = await albumsService.fetchAlbumTracks(albumId, { limit, offset });
    const data = response.data;
    const items = Array.isArray(data?.items) ? data.items : [];

    for (const track of items) {
      const trackId = typeof track?.id === 'string' ? track.id.trim() : '';
      if (trackId) {
        uniqueIds.add(trackId);
      }
    }

    if (!data?.next) {
      hasMore = false;
    } else {
      const nextOffset = (data.offset ?? offset) + (data.limit ?? limit);
      if (nextOffset <= offset) {
        hasMore = false;
      } else {
        offset = nextOffset;
      }
    }
  }

  return Array.from(uniqueIds);
};

const chunkArray = <T,>(items: readonly T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size) as T[]);
  }
  return chunks;
};

const cascadeSaveTracks = async (trackIds: string[]): Promise<number> => {
  const chunks = chunkArray(trackIds, TRACK_BATCH_SIZE);
  let savedCount = 0;

  for (let index = 0; index < chunks.length; index += 1) {
    const chunk = chunks[index];
    await userService.saveTracks(chunk);
    savedCount += chunk.length;
    const moreRemaining = index < chunks.length - 1;
    if (moreRemaining) {
      await delay(TRACK_SAVE_BATCH_DELAY_MS);
    }
  }

  return savedCount;
};

const cascadeDeleteTracks = async (trackIds: string[]): Promise<number> => {
  const chunks = chunkArray(trackIds, TRACK_BATCH_SIZE);
  let deletedCount = 0;

  for (const chunk of chunks) {
    await userService.deleteTracks(chunk);
    deletedCount += chunk.length;
  }

  return deletedCount;
};

const PLAYLIST_CASCADE_PAGE_SIZE = 100;
const PLAYLIST_CASCADE_MAX_ITEMS = 10000;
const PLAYLIST_RECORDING_BATCH_SIZE = 400;

type PlaylistCascadeTargets = {
  trackIds: string[];
  recordingIds: string[];
  totalItems: number;
};

const collectPlaylistCascadeTargets = async (
  playlistId: string,
  state: RootState
): Promise<PlaylistCascadeTargets> => {
  const trackIds = new Set<string>();
  const recordingIds = new Set<string>();
  const playlistState = state.playlist;
  const isActivePlaylist = playlistState?.playlist?.id === playlistId;
  const loadedTracks = isActivePlaylist ? playlistState.tracks : [];
  const totalTracks = isActivePlaylist ? playlistState.playlist?.tracks?.total : undefined;
  const enforceLimit = () => {
    if (typeof totalTracks === 'number' && totalTracks > PLAYLIST_CASCADE_MAX_ITEMS) {
      throw new Error(
        `Playlist has too many items to cascade (limit ${PLAYLIST_CASCADE_MAX_ITEMS}).`
      );
    }
  };

  enforceLimit();

  let processed = 0;

  const appendFromTrack = (track: any) => {
    const trackId = typeof track?.id === 'string' ? track.id.trim() : '';
    if (trackId) {
      trackIds.add(trackId);
    }
    const recordingId = resolveTrackRecordingId(track);
    if (recordingId && typeof recordingId === 'string' && recordingId.trim().length > 0) {
      recordingIds.add(recordingId.trim());
    }
  };

  const appendFromItem = (item: any) => {
    appendFromTrack((item as any)?.track ?? item);
    processed += 1;
    if (processed > PLAYLIST_CASCADE_MAX_ITEMS) {
      throw new Error(
        `Playlist has too many items to cascade (limit ${PLAYLIST_CASCADE_MAX_ITEMS}).`
      );
    }
  };

  loadedTracks.forEach(appendFromItem);

  let offset = loadedTracks.length;
  const limit = PLAYLIST_CASCADE_PAGE_SIZE;

  while (true) {
    if (typeof totalTracks === 'number' && offset >= totalTracks) {
      break;
    }

    const { data } = await playlistService.getPlaylistItems(playlistId, { offset, limit });
    const items = Array.isArray(data?.items) ? data.items : [];
    if (!items.length) {
      break;
    }

    items.forEach(appendFromItem);

    if (!data?.next) {
      break;
    }

    offset += items.length;
    enforceLimit();
  }

  return {
    trackIds: Array.from(trackIds),
    recordingIds: Array.from(recordingIds),
    totalItems: processed,
  };
};

const cascadeSaveRecordingIds = async (recordingIds: string[]): Promise<number> => {
  const chunks = chunkArray(recordingIds, PLAYLIST_RECORDING_BATCH_SIZE);
  let saved = 0;
  for (const chunk of chunks) {
    const result = await favoriteRecordingsService.saveFavoritesBatch(chunk);
    saved += typeof result.saved === 'number' ? result.saved : result.recordings.length;
  }
  return saved;
};

const cascadeDeleteRecordingIds = async (recordingIds: string[]): Promise<number> => {
  const chunks = chunkArray(recordingIds, PLAYLIST_RECORDING_BATCH_SIZE);
  let deleted = 0;
  for (const chunk of chunks) {
    deleted += await favoriteRecordingsService.removeFavoritesBatch(chunk);
  }
  return deleted;
};

export const YOUR_LIBRARY_TTL_MS = 60 * 60 * 1000;
export const RECORDINGS_REFRESH_TTL_MS = 60 * 60 * 1000;
export const WORKS_REFRESH_TTL_MS = 60 * 60 * 1000;
export const PLAYLISTS_REFRESH_TTL_MS = YOUR_LIBRARY_TTL_MS;
export const ALBUMS_REFRESH_TTL_MS = YOUR_LIBRARY_TTL_MS;
export const ARTISTS_REFRESH_TTL_MS = YOUR_LIBRARY_TTL_MS;

type AlbumRecordingTarget = {
  recordingId: string;
  workId?: string;
  trackIds: string[];
};

type AlbumRecordingCollection = {
  recordings: AlbumRecordingTarget[];
  totalTracks: number;
};

type AlbumRecordingSaveSummary = {
  totalTracks: number;
  totalRecordings: number;
  saved: number;
  skipped: number;
};

type AlbumRecordingRemoveSummary = {
  totalTracks: number;
  totalRecordings: number;
  removed: number;
  skipped: number;
};

const collectAlbumRecordingIds = async (
  albumId: string,
  preloadedTrackIds?: string[]
): Promise<AlbumRecordingCollection> => {
  const normalizedTrackIds =
    preloadedTrackIds && Array.isArray(preloadedTrackIds) && preloadedTrackIds.length > 0
      ? Array.from(
          new Set(
            preloadedTrackIds
              .map((id) => (typeof id === 'string' ? id.trim() : ''))
              .filter((id) => id.length > 0)
          )
        )
      : await collectAlbumTrackIds(albumId);
  const trackIds = normalizedTrackIds;
  if (!trackIds.length) {
    return { recordings: [], totalTracks: 0 };
  }

  const matches = await recordingsService.fetchTrackMatches(trackIds);
  const recordingsMap = new Map<
    string,
    {
      workId?: string;
      trackIds: Set<string>;
    }
  >();

  for (const trackId of trackIds) {
    const match = matches?.[trackId];
    if (!match || typeof match !== 'object') continue;
    const recordingId =
      typeof match.recordingId === 'string' && match.recordingId.trim().length > 0
        ? match.recordingId.trim()
        : null;
    if (!recordingId) continue;

    if (
      typeof match.spotifyAlbumId === 'string' &&
      match.spotifyAlbumId.trim().length > 0 &&
      match.spotifyAlbumId.trim() !== albumId
    ) {
      continue;
    }

    const matchAny = match as Record<string, unknown> | Record<string, any>;
    const resolvedWorkId =
      stringOrUndefined((matchAny as any).workId) ??
      stringOrUndefined((matchAny as any).work_id);
    const existing = recordingsMap.get(recordingId);
    if (existing) {
      existing.trackIds.add(trackId);
      if (!existing.workId && resolvedWorkId) {
        existing.workId = resolvedWorkId;
      }
      continue;
    }

    recordingsMap.set(recordingId, {
      workId: resolvedWorkId,
      trackIds: new Set<string>([trackId])
    });
  }

  const recordings = Array.from(recordingsMap.entries()).map(
    ([recordingId, value]): AlbumRecordingTarget => ({
      recordingId,
      workId: value.workId,
      trackIds: Array.from(value.trackIds)
    })
  );

  return { recordings, totalTracks: trackIds.length };
};

const cascadeSaveRecordings = async (
  dispatch: AppDispatch,
  albumId: string,
  preloadedTrackIds?: string[],
  options?: { skipTrackAutomation?: boolean }
): Promise<AlbumRecordingSaveSummary> => {
  const { recordings, totalTracks } = await collectAlbumRecordingIds(albumId, preloadedTrackIds);
  if (!recordings.length) {
    return {
      totalTracks,
      totalRecordings: 0,
      saved: 0,
      skipped: totalTracks,
    };
  }

  const workIds = Array.from(
    new Set(
      recordings
        .map((entry) => entry.workId)
        .filter((workId): workId is string => typeof workId === 'string' && workId.length > 0)
    )
  );

  let blockedWorkIds = new Set<string>();
  if (workIds.length > 0) {
    try {
      blockedWorkIds = await favoriteActionEventsService.getBlockedEntities('work', workIds, {
        blockedActions: ['unsave', 'mute'],
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Failed to load blocked work actions; proceeding without filter', error);
      }
    }
  }

  const filteredRecordings =
    blockedWorkIds.size > 0
      ? recordings.filter((entry) => !entry.workId || !blockedWorkIds.has(entry.workId))
      : recordings;

  if (filteredRecordings.length === 0) {
    const totalRecordings = recordings.length;
    const missingMappings = Math.max(totalTracks - totalRecordings, 0);
    const skipped = missingMappings + totalRecordings;
    return {
      totalTracks,
      totalRecordings,
      saved: 0,
      skipped,
    };
  }

  const recordingIds = filteredRecordings.map((entry) => entry.recordingId);
  const workEntries = filteredRecordings
    .filter((entry) => entry.workId)
    .reduce<Record<string, string>>((accumulator, entry) => {
      if (entry.workId) {
        accumulator[entry.recordingId] = entry.workId;
      }
      return accumulator;
    }, {});

  const trackEntries = filteredRecordings.reduce<Record<string, string[]>>((accumulator, entry) => {
    if (entry.trackIds && entry.trackIds.length > 0) {
      accumulator[entry.recordingId] = entry.trackIds;
    }
    return accumulator;
  }, {});

  const cascadeResult = await dispatch(
    saveRecordingsWithCascade({
      recordingIds,
      options: {
        workIdsByRecording: Object.keys(workEntries).length ? workEntries : undefined,
        resolveMissingWorkIds: Object.keys(workEntries).length === 0,
        trackIdsByRecording: Object.keys(trackEntries).length ? trackEntries : undefined,
        skipTrackAutomation: options?.skipTrackAutomation === true,
      },
    })
  ).unwrap();

  const totalRecordings = recordings.length;
  const missingMappings = Math.max(totalTracks - totalRecordings, 0);
  const blockedCount = recordings.length - filteredRecordings.length;
  const skipped = missingMappings + Math.max(cascadeResult.skipped, 0) + blockedCount;

  return {
    totalTracks,
    totalRecordings,
    saved: cascadeResult.saved,
    skipped,
  };
};

const cascadeDeleteRecordings = async (
  dispatch: AppDispatch,
  albumId: string,
  preloadedTrackIds?: string[],
  options?: { skipTrackAutomation?: boolean }
): Promise<AlbumRecordingRemoveSummary> => {
  const { recordings, totalTracks } = await collectAlbumRecordingIds(albumId, preloadedTrackIds);
  if (!recordings.length) {
    return {
      totalTracks,
      totalRecordings: 0,
      removed: 0,
      skipped: totalTracks,
    };
  }

  const recordingIds = recordings.map((entry) => entry.recordingId);
  const trackEntries = recordings.reduce<Record<string, string[]>>((accumulator, entry) => {
    if (entry.trackIds && entry.trackIds.length > 0) {
      accumulator[entry.recordingId] = entry.trackIds;
    }
    return accumulator;
  }, {});

  const cascadeResult = await dispatch(
    removeRecordingsWithCascade({
      recordingIds,
      options: {
        trackIdsByRecording: Object.keys(trackEntries).length ? trackEntries : undefined,
        skipTrackAutomation: options?.skipTrackAutomation === true,
      },
    })
  ).unwrap();

  const totalRecordings = recordings.length;
  const missingMappings = Math.max(totalTracks - totalRecordings, 0);
  const skipped = missingMappings + Math.max(cascadeResult.skipped, 0);

  return {
    totalTracks,
    totalRecordings,
    removed: cascadeResult.removed,
    skipped,
  };
};

export type LibraryLastFetchedMap = {
  albums: number | null;
  artists: number | null;
  playlists: number | null;
  composers: number | null;
  works: number | null;
  recordings: number | null;
};

export interface YourLibraryState {
  myAlbums: Album[];
  myArtists: Artist[];
  myPlaylists: Playlist[];
  myComposers: LibraryComposer[];
  myWorks: LibraryWork[];
  myRecordings: LibraryRecording[];

  search: string;
  view: 'GRID' | 'LIST' | 'COMPACT';
  orderBy: 'name' | 'added_at' | 'default';
  composerSortOrder: 'default' | 'relevance';
  filter: LibraryFilter;
  recordingsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  recordingsFetchInFlight: boolean;
  artistsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  artistsFetchInFlight: boolean;
  albumsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  albumsFetchInFlight: boolean;
  albumsSyncStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  worksStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  worksFetchInFlight: boolean;
  playlistsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  playlistsFetchInFlight: boolean;
  albumsError: string | null;
  albumsPageCursor: string | null;
  albumsPageSessionId: string | null;
  composersSourceSignature: string | null;
  composersSourceUserId: string | null;
  librarySyncSettings: LibrarySyncSettingsState;
  lastFetchedAt: LibraryLastFetchedMap;
  importStatus?: 'idle' | 'pending' | 'succeeded' | 'failed';
  importError?: string | null;
  lastImportStartedAt: number | null;
  lastImportRequiresAlbumSync: boolean | null;
  lastImportRequiresTrackSync: boolean | null;
  lastImportCompletedAt: number | null;
  lastRecordingsAndWorksSyncStartedAt: number | null;
  importSyncSelections: {
    importLibrary: boolean;
    syncRecordings: boolean;
    syncTrackRecordings: boolean;
  };
}

const UNKNOWN_COMPOSER = 'Unknown Composer';
const UNKNOWN_WORK = 'Unknown Work';
const UNKNOWN_RECORDING = 'Untitled Recording';
const UNKNOWN_PERFORMER = 'Unknown Performer';
const UNKNOWN_ALBUM = 'Unknown Album';

const markFetched = (state: YourLibraryState, key: keyof LibraryLastFetchedMap) => {
  state.lastFetchedAt[key] = Date.now();
};

const isRecordingsAndWorksJobRunning = (job?: RecordingsAndWorksSyncJobStatus | null): boolean =>
  Boolean(job && (job.status === 'queued' || job.status === 'running'));

const isRecordingsAndWorksJobComplete = (job?: RecordingsAndWorksSyncJobStatus | null): boolean =>
  Boolean(job && (job.status === 'succeeded' || job.status === 'failed'));

const applyRecordingsAndWorksJobUpdate = (
  state: YourLibraryState,
  job: RecordingsAndWorksSyncJobStatus | null
) => {
  state.librarySyncSettings.recordingsAndWorksJob = job;
  const isRunning = isRecordingsAndWorksJobRunning(job);
  const isComplete = isRecordingsAndWorksJobComplete(job);

  state.librarySyncSettings.isSyncingAlbumRecordingsAndWorks = isRunning;
  state.librarySyncSettings.isSyncingTrackRecordings = isRunning;
  state.librarySyncSettings.recordingsAndWorksJobId = isComplete ? null : job?.jobId ?? null;

  if (!job) {
    return;
  }

  if (isComplete) {
    const nowIso = new Date().toISOString();
    const nextValue = cloneLibrarySyncSettingsValue(state.librarySyncSettings.value);
    const statusLabel = job.status === 'succeeded' ? 'success' : 'failed';
    if (job.recordings || job.albumRecordings) {
      nextValue.oneOffs.syncAlbumRecordings.lastRunAt = nowIso;
      nextValue.oneOffs.syncAlbumRecordings.lastStatus = statusLabel;
    }
    nextValue.oneOffs.syncRecordingWorks.lastRunAt = nowIso;
    nextValue.oneOffs.syncRecordingWorks.lastStatus = statusLabel;
    if ('syncTrackRecordings' in nextValue.oneOffs && job.trackRecordings) {
      nextValue.oneOffs.syncTrackRecordings.lastRunAt = nowIso;
      nextValue.oneOffs.syncTrackRecordings.lastStatus = statusLabel;
    }
    state.librarySyncSettings.value = nextValue;
    state.librarySyncSettings.lastFetchedAt = Date.now();
    state.librarySyncSettings.error =
      statusLabel === 'failed'
        ? job.error?.trim() || 'Failed to sync recordings and works.'
        : null;
  } else {
    state.librarySyncSettings.error = null;
  }
};

const upsertById = <T extends { id?: string }>(target: T[], items: T[]) => {
  const indexById = new Map<string, number>();
  target.forEach((item, idx) => {
    if (item?.id) {
      indexById.set(item.id, idx);
    }
  });

  for (const item of items) {
    const id = item?.id;
    if (!id) continue;

    const existingIndex = indexById.get(id);
    if (existingIndex === undefined) {
      target.push(item);
      indexById.set(id, target.length - 1);
      continue;
    }

    target[existingIndex] = item;
  }
};

const stringOrUndefined = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const numberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const firstImageUrl = (images: Array<{ url?: string | null }> | undefined): string | undefined => {
  if (!Array.isArray(images) || !images.length) return undefined;
  const candidate = images.find((entry) => typeof entry?.url === 'string' && entry.url.trim().length > 0);
  return candidate?.url ? candidate.url.trim() : undefined;
};

const normalizeSpotifyTrackId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed.length) {
    return undefined;
  }

  const uriMatch = trimmed.match(/^spotify:track:([0-9A-Za-z]{22})$/i);
  if (uriMatch?.[1]) {
    return uriMatch[1];
  }

  const urlMatch = trimmed.match(/track\/([0-9A-Za-z]{22})(?:\?.*)?$/i);
  if (urlMatch?.[1]) {
    return urlMatch[1];
  }

  if (/^[0-9A-Za-z]{22}$/.test(trimmed)) {
    return trimmed;
  }

  return undefined;
};

const appendTrackIdsByRecording = (
  target: Map<string, Set<string>>,
  recordingId: string,
  values: Iterable<string>
) => {
  const trimmedId = typeof recordingId === 'string' ? recordingId.trim() : '';
  if (!trimmedId) return;

  let bucket = target.get(trimmedId);
  if (!bucket) {
    bucket = new Set<string>();
    target.set(trimmedId, bucket);
  }

  for (const raw of values) {
    const normalized = normalizeSpotifyTrackId(raw);
    if (normalized) {
      bucket.add(normalized);
    }
  }
};

const resolveComposerNameFromComposer = (composer: Record<string, any>): string | undefined => {
  return (
    stringOrUndefined(composer.name) ??
    stringOrUndefined(composer.displayName) ??
    stringOrUndefined(composer.display_name) ??
    stringOrUndefined(composer.fullName) ??
    stringOrUndefined(composer.full_name) ??
    stringOrUndefined(composer.sortableName) ??
    stringOrUndefined(composer.sortable_name) ??
    stringOrUndefined(composer.composerName) ??
    stringOrUndefined(composer.composer_name) ??
    stringOrUndefined(composer.primaryName) ??
    stringOrUndefined(composer.primary_name) ??
    stringOrUndefined(composer.label)
  );
};

const extractComposerName = (work: Record<string, any>): string | undefined => {
  const direct =
    stringOrUndefined(work.composerName) ??
    stringOrUndefined(work.composer_name) ??
    stringOrUndefined(work.composerDisplayName) ??
    stringOrUndefined(work.composer_display_name) ??
    stringOrUndefined(work.composerFullName) ??
    stringOrUndefined(work.composer_full_name) ??
    stringOrUndefined(work.composerLabel) ??
    stringOrUndefined(work.composer_label) ??
    stringOrUndefined(work.composer);
  if (direct) return direct;

  const composer = work.composer;
  if (composer && typeof composer === 'object') {
    const resolved = resolveComposerNameFromComposer(composer);
    if (resolved) return resolved;
  }

  const composers = work.composers;
  if (Array.isArray(composers)) {
    for (const entry of composers) {
      if (entry && typeof entry === 'object') {
        const resolved = resolveComposerNameFromComposer(entry);
        if (resolved) return resolved;
      } else {
        const resolved = stringOrUndefined(entry);
        if (resolved) return resolved;
      }
    }
  }

  return undefined;
};

const extractMovementCount = (work: Record<string, any>): number | undefined => {
  return (
    numberOrUndefined(work.movementCount) ??
    numberOrUndefined(work.movement_count) ??
    numberOrUndefined(work.movementsCount) ??
    numberOrUndefined(work.movementTotal) ??
    numberOrUndefined(work.movement_total) ??
    (Array.isArray(work.movements) ? work.movements.length : undefined)
  );
};

const extractRecordingCount = (work: Record<string, any>): number | undefined => {
  return (
    numberOrUndefined(work.recordingCount) ??
    numberOrUndefined(work.recording_count) ??
    numberOrUndefined(work.recordingsCount) ??
    numberOrUndefined(work.recordings_count) ??
    numberOrUndefined(work.recordingTotal) ??
    numberOrUndefined(work.recording_total)
  );
};

const extractCatalogueNumber = (work: Record<string, any>): string | undefined => {
  return (
    stringOrUndefined(work.catalogueNumber) ??
    stringOrUndefined(work.catalogNumber) ??
    stringOrUndefined(work.catalogue) ??
    stringOrUndefined(work.catalog) ??
    stringOrUndefined(work.catalogDisplay) ??
    stringOrUndefined(work.catalog_display) ??
    stringOrUndefined(work.catalogNo) ??
    stringOrUndefined(work.catalog_no)
  );
};

const extractCatalogueNumberNormalized = (work: Record<string, any>): string | undefined => {
  const catalog = work.catalog;
  const catalogue = work.catalogue;
  const nestedCatalogNormalized =
    catalog && typeof catalog === 'object'
      ? stringOrUndefined((catalog as Record<string, any>).normalized) ??
        stringOrUndefined((catalog as Record<string, any>).normalised) ??
        stringOrUndefined((catalog as Record<string, any>).numberNormalized) ??
        stringOrUndefined((catalog as Record<string, any>).number_normalized)
      : undefined;
  const nestedCatalogueNormalized =
    catalogue && typeof catalogue === 'object'
      ? stringOrUndefined((catalogue as Record<string, any>).normalized) ??
        stringOrUndefined((catalogue as Record<string, any>).normalised) ??
        stringOrUndefined((catalogue as Record<string, any>).numberNormalized) ??
        stringOrUndefined((catalogue as Record<string, any>).number_normalized)
      : undefined;

  return (
    stringOrUndefined(work.numberNormalized) ??
    stringOrUndefined(work.number_normalized) ??
    stringOrUndefined(work.catalogueNumberNormalized) ??
    stringOrUndefined(work.catalogue_number_normalized) ??
    stringOrUndefined(work.catalogNumberNormalized) ??
    stringOrUndefined(work.catalog_number_normalized) ??
    stringOrUndefined(work.catalogNormalized) ??
    stringOrUndefined(work.catalog_normalized) ??
    nestedCatalogNormalized ??
    nestedCatalogueNormalized
  );
};

const extractImageUrl = (work: Record<string, any>): string | undefined => {
  const candidates = [
    work.imageUrl,
    work.image_url,
    work.coverUrl,
    work.cover_url,
    work.heroImage?.url,
    work.hero_image?.url,
    work.primaryImage?.url,
    work.primary_image?.url,
  ];

  for (const candidate of candidates) {
    const resolved = stringOrUndefined(candidate);
    if (resolved) return resolved;
  }

  if (Array.isArray(work.images)) {
    for (const item of work.images) {
      const resolved = stringOrUndefined(item?.url);
      if (resolved) return resolved;
    }
  }

  return undefined;
};

const extractUri = (work: Record<string, any>, workId: string): string => {
  return (
    stringOrUndefined(work.uri) ??
    stringOrUndefined(work.spotifyUri) ??
    stringOrUndefined(work.spotify_uri) ??
    `cmf:work:${workId}`
  );
};

const extractName = (work: Record<string, any>): string => {
  return (
    stringOrUndefined(work.title) ??
    stringOrUndefined(work.name) ??
    stringOrUndefined(work.displayTitle) ??
    stringOrUndefined(work.display_name) ??
    'Untitled Work'
  );
};

const extractComposerId = (work: Record<string, any>): string | undefined => {
  const direct =
    stringOrUndefined(work.composerId) ??
    stringOrUndefined(work.composer_id) ??
    stringOrUndefined(work.catalogComposerId) ??
    stringOrUndefined(work.catalog_composer_id);
  if (direct) return direct;

  const composer = work.composer;
  if (composer && typeof composer === 'object') {
    return (
      stringOrUndefined(composer.id) ??
      stringOrUndefined(composer.composerId) ??
      stringOrUndefined(composer.composer_id)
    );
  }

  return undefined;
};

const buildLibraryWorkFromCatalog = (
  work: Record<string, any>,
  fallbackId: string,
  savedAt: string
): LibraryWork => {
  const resolvedId =
    stringOrUndefined(work.id) ??
    stringOrUndefined(work.workId) ??
    stringOrUndefined(work.slug) ??
    fallbackId;
  const name = extractName(work);
  const composerName = extractComposerName(work) ?? UNKNOWN_COMPOSER;
  const movementCount = extractMovementCount(work);
  const imageUrl = extractImageUrl(work);
  const catalogueNumber = extractCatalogueNumber(work);
  const catalogueNumberNormalized = extractCatalogueNumberNormalized(work);
  const composerId = extractComposerId(work);
  const recordingCount = extractRecordingCount(work);
  const uri = extractUri(work, resolvedId);

  const libraryWork: LibraryWork = {
    id: resolvedId,
    uri,
    type: 'work',
    name,
    composerName,
    savedAt,
  };

  if (composerId) {
    libraryWork.composerId = composerId;
  }
  if (typeof movementCount === 'number') {
    libraryWork.movementCount = movementCount;
  }
  if (typeof recordingCount === 'number') {
    libraryWork.recordingCount = recordingCount;
  }
  if (imageUrl) {
    libraryWork.imageUrl = imageUrl;
  }
  if (catalogueNumberNormalized) {
    libraryWork.catalogueNumberNormalized = catalogueNumberNormalized;
  }
  if (catalogueNumber) {
    libraryWork.catalogueNumber = catalogueNumber;
  } else if (catalogueNumberNormalized) {
    libraryWork.catalogueNumber = catalogueNumberNormalized;
  }

  return libraryWork;
};

const buildFallbackLibraryWork = (workId: string, savedAt: string): LibraryWork => ({
  id: workId,
  uri: `cmf:work:${workId}`,
  type: 'work',
  name: workId,
  composerName: UNKNOWN_COMPOSER,
  savedAt,
});

const derivePrimaryWorkId = (recording: Record<string, any>): string | undefined => {
  const works = recording?.works;
  if (!Array.isArray(works)) return undefined;

  let bestEntry: { id: string; confidence: number } | undefined;

  for (const entry of works) {
    if (!entry || typeof entry !== 'object') continue;
    const workId =
      stringOrUndefined((entry as any).workId) ??
      stringOrUndefined((entry as any).id) ??
      stringOrUndefined((entry as any).work_id);
    if (!workId) continue;

    const confidence = numberOrUndefined((entry as any).confidence) ?? 0;
    if (!bestEntry || confidence > bestEntry.confidence) {
      bestEntry = { id: workId, confidence };
    }
  }

  return bestEntry?.id;
};


type SaveRecordingsWithCascadeArgs = {
  recordingIds: string[];
  options?: {
    notes?: string;
    labels?: string[];
    savedAt?: string | number | Date;
    id?: string;
    workIdsByRecording?: Record<string, string | undefined>;
    resolveMissingWorkIds?: boolean;
    trackIdsByRecording?: Record<string, string[] | null | undefined>;
    skipTrackAutomation?: boolean;
    cascades?: {
      tracks?: boolean;
    };
  };
};

type SaveRecordingsWithCascadeResult = {
  favorites: FavoriteRecording[];
  cascadedWorkIds: string[];
  requestedRecordingIds: string[];
  saved: number;
  skipped: number;
  cascadedTrackCount: number;
  trackAutomationAttempted: boolean;
};

type RemoveRecordingsWithCascadeArgs = {
  recordingIds: string[];
  options?: {
    trackIdsByRecording?: Record<string, string[] | null | undefined>;
    skipTrackAutomation?: boolean;
    cascades?: {
      tracks?: boolean;
    };
  };
};

type RemoveRecordingsWithCascadeResult = {
  requestedRecordingIds: string[];
  removed: number;
  skipped: number;
  cascadedTrackCount: number;
  trackAutomationAttempted: boolean;
};

export const saveRecordingsWithCascade = createAsyncThunk<
  SaveRecordingsWithCascadeResult,
  SaveRecordingsWithCascadeArgs,
  { state: RootState; rejectValue: string }
>(
  'yourLibrary/saveRecordingsWithCascade',
  async ({ recordingIds, options }, { getState, rejectWithValue }) => {
    const normalizedIds = Array.from(
      new Set(
        (recordingIds ?? [])
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0)
      )
    );

    if (!normalizedIds.length) {
      const errorMessage = 'Recording IDs are required.';
      return rejectWithValue(errorMessage);
    }

    const state = getState();
    const automations = state.yourLibrary.librarySyncSettings.value.automations;
    const workAutomationEnabled = automations.recordingSaveCascade.enabled;
    const trackAutomationEnabled = automations.recordingSaveTracks?.enabled ?? false;
    const skipTrackAutomation = options?.skipTrackAutomation === true;
    const overrideTracks = options?.cascades?.tracks;
    const shouldCascadeTracks =
      !skipTrackAutomation && (overrideTracks ?? trackAutomationEnabled);

    const detailCache: RecordingDetailCache = new Map();
    const workIdsByRecording = new Map<string, string>();
    const trackIdSetsByRecording = new Map<string, Set<string>>();

    if (options?.workIdsByRecording) {
      for (const [recordingId, workId] of Object.entries(options.workIdsByRecording)) {
        if (typeof recordingId === 'string' && recordingId.trim().length > 0 && workId) {
          workIdsByRecording.set(recordingId.trim(), workId);
        }
      }
    }

    if (options?.trackIdsByRecording) {
      for (const [recordingId, candidates] of Object.entries(options.trackIdsByRecording)) {
        if (!candidates) continue;
        if (Array.isArray(candidates)) {
          appendTrackIdsByRecording(trackIdSetsByRecording, recordingId, candidates);
        } else if (typeof candidates === 'string') {
          appendTrackIdsByRecording(trackIdSetsByRecording, recordingId, [candidates]);
        }
      }
    }

    const shouldResolveMissingWorkIds =
      options?.resolveMissingWorkIds !== false &&
      workAutomationEnabled &&
      normalizedIds.length === 1 &&
      !workIdsByRecording.has(normalizedIds[0]);

    if (shouldResolveMissingWorkIds) {
      const resolvedWorkId = await resolveCanonicalRecordingWorkId(normalizedIds[0], detailCache);
      if (resolvedWorkId) {
        workIdsByRecording.set(normalizedIds[0], resolvedWorkId);
      }
    }

    let favorites: FavoriteRecording[] = [];
    let savedCount = 0;
    let skippedCount = 0;

    if (normalizedIds.length === 1) {
      const recordingId = normalizedIds[0];
      const savePayload: {
        workId?: string;
        notes?: string;
        labels?: string[];
        savedAt?: string | number | Date;
        id?: string;
      } = {};

      const workId = workIdsByRecording.get(recordingId);
      if (workId) {
        savePayload.workId = workId;
      }
      if (options?.notes) {
        savePayload.notes = options.notes;
      }
      if (Array.isArray(options?.labels) && options.labels.length > 0) {
        savePayload.labels = options.labels;
      }
      if (options && Object.prototype.hasOwnProperty.call(options, 'savedAt')) {
        savePayload.savedAt = options?.savedAt;
      }
      if (options?.id) {
        savePayload.id = options.id;
      }

      try {
        const favorite = await favoriteRecordingsService.saveFavorite(recordingId, savePayload);
        favorites = [favorite];
        savedCount = 1;
      } catch (error) {
        const fallbackMessage = 'Failed to save recording.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        return rejectWithValue(errorMessage);
      }
    } else {
      const batchOptions: {
        notes?: string;
        labels?: string[];
        savedAt?: string | number | Date;
      } = {};

      if (options?.notes) {
        batchOptions.notes = options.notes;
      }
      if (Array.isArray(options?.labels) && options.labels.length > 0) {
        batchOptions.labels = options.labels;
      }
      if (options && Object.prototype.hasOwnProperty.call(options, 'savedAt')) {
        batchOptions.savedAt = options?.savedAt;
      }

      try {
        const outcome = await favoriteRecordingsService.saveFavoritesBatch(normalizedIds, batchOptions);
        favorites = outcome.recordings;
        savedCount = typeof outcome.saved === 'number' ? outcome.saved : outcome.recordings.length;
        skippedCount = typeof outcome.skipped === 'number' ? outcome.skipped : 0;
      } catch (error) {
        const fallbackMessage = 'Failed to save recordings.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        return rejectWithValue(errorMessage);
      }
    }

    if (favorites.length > 0) {
      favorites = favorites.map((favorite) => {
        const mappedWorkId = workIdsByRecording.get(favorite.recordingId);
        if (mappedWorkId && !favorite.workId) {
          return {
            ...favorite,
            workId: mappedWorkId,
          };
        }
        return favorite;
      });
    }

    const cascadedWorkIds: string[] = [];
    if (workAutomationEnabled && workIdsByRecording.size > 0) {
      const uniqueWorkIds = new Set(workIdsByRecording.values());
      for (const workId of uniqueWorkIds) {
        cascadedWorkIds.push(workId);
      }

      for (const [recordingId, workId] of workIdsByRecording.entries()) {
        if (!workId) continue;
        try {
          await favoriteWorksService.saveFavoriteWork(workId);
        } catch (error: any) {
          const status = error?.response?.status;
          if (status !== 409 && status !== 405 && process.env.NODE_ENV === 'development') {
            console.warn(
              '[yourLibrary] Failed to cascade work favorite for recording',
              recordingId,
              workId,
              error
            );
          }
        }
      }
    }

    let cascadedTrackCount = 0;
    let trackAutomationAttempted = false;

    if (shouldCascadeTracks) {
      trackAutomationAttempted = true;
      const aggregateTrackIds = new Set<string>();

      for (const recordingId of normalizedIds) {
        let trackSet = trackIdSetsByRecording.get(recordingId);
        if (!trackSet || trackSet.size === 0) {
          const resolvedTrackIds = await resolveRecordingTrackIds(recordingId, detailCache);
          if (resolvedTrackIds.length > 0) {
            trackSet = new Set<string>(resolvedTrackIds);
            trackIdSetsByRecording.set(recordingId, trackSet);
          }
        }

        if (trackSet) {
          for (const trackId of trackSet) {
            aggregateTrackIds.add(trackId);
          }
        }
      }

      if (aggregateTrackIds.size > 0) {
        try {
          cascadedTrackCount = await cascadeSaveTracks(Array.from(aggregateTrackIds));
          if (cascadedTrackCount > 0) {
            const noun = cascadedTrackCount === 1 ? 'track' : 'tracks';
            const scope = normalizedIds.length === 1 ? 'this recording' : 'these recordings';
            void message.success(`Also saved ${cascadedTrackCount} ${noun} from ${scope}`);
          }
        } catch (error) {
          cascadedTrackCount = 0;
          if (process.env.NODE_ENV === 'development') {
            console.warn('[yourLibrary] Failed to cascade recording tracks', normalizedIds, error);
          }
          void message.warning(
            'Saved the recording, but we could not save its tracks. Please try again in a bit.'
          );
        }
      }
    }

    return {
      favorites,
      cascadedWorkIds,
      requestedRecordingIds: normalizedIds,
      saved: savedCount > 0 ? savedCount : favorites.length,
      skipped: skippedCount,
      cascadedTrackCount,
      trackAutomationAttempted,
    };
  }
);

export const removeRecordingsWithCascade = createAsyncThunk<
  RemoveRecordingsWithCascadeResult,
  RemoveRecordingsWithCascadeArgs,
  { state: RootState; rejectValue: string }
>(
  'yourLibrary/removeRecordingsWithCascade',
  async ({ recordingIds, options }, { getState, rejectWithValue }) => {
    const normalizedIds = Array.from(
      new Set(
        (recordingIds ?? [])
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0)
      )
    );

    if (!normalizedIds.length) {
      const errorMessage = 'Recording IDs are required.';
      return rejectWithValue(errorMessage);
    }

    const state = getState();
    const automations = state.yourLibrary.librarySyncSettings.value.automations;
    const trackAutomationEnabled = automations.recordingUnsaveTracks?.enabled ?? false;
    const skipTrackAutomation = options?.skipTrackAutomation === true;
    const overrideTracks = options?.cascades?.tracks;
    const shouldCascadeTracks =
      !skipTrackAutomation && (overrideTracks ?? trackAutomationEnabled);

    const detailCache: RecordingDetailCache = new Map();
    const trackIdSetsByRecording = new Map<string, Set<string>>();

    if (options?.trackIdsByRecording) {
      for (const [recordingId, candidates] of Object.entries(options.trackIdsByRecording)) {
        if (!candidates) continue;
        if (Array.isArray(candidates)) {
          appendTrackIdsByRecording(trackIdSetsByRecording, recordingId, candidates);
        } else if (typeof candidates === 'string') {
          appendTrackIdsByRecording(trackIdSetsByRecording, recordingId, [candidates]);
        }
      }
    }

    let removedCount = 0;

    if (normalizedIds.length === 1) {
      const recordingId = normalizedIds[0];
      try {
        await favoriteRecordingsService.removeFavorite(recordingId);
        removedCount = 1;
      } catch (error) {
        const fallbackMessage = 'Failed to remove recording.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        return rejectWithValue(errorMessage);
      }
    } else {
      try {
        const deleted = await favoriteRecordingsService.removeFavoritesBatch(normalizedIds);
        removedCount = typeof deleted === 'number' && Number.isFinite(deleted) ? deleted : 0;
      } catch (error) {
        const fallbackMessage = 'Failed to remove recordings.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        return rejectWithValue(errorMessage);
      }
    }

    const skippedCount = Math.max(normalizedIds.length - removedCount, 0);

    let cascadedTrackCount = 0;
    let trackAutomationAttempted = false;

    if (shouldCascadeTracks && removedCount > 0) {
      trackAutomationAttempted = true;

      for (const recordingId of normalizedIds) {
        let trackSet = trackIdSetsByRecording.get(recordingId);
        if (!trackSet || trackSet.size === 0) {
          try {
            const resolvedTrackIds = await resolveRecordingTrackIds(recordingId, detailCache);
            if (resolvedTrackIds.length > 0) {
              appendTrackIdsByRecording(trackIdSetsByRecording, recordingId, resolvedTrackIds);
              trackSet = trackIdSetsByRecording.get(recordingId);
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                '[yourLibrary] Failed to resolve tracks for recording removal',
                recordingId,
                error
              );
            }
          }
        }
      }

      const aggregateTrackIds = new Set<string>();
      for (const set of trackIdSetsByRecording.values()) {
        for (const trackId of set) {
          aggregateTrackIds.add(trackId);
        }
      }

      if (aggregateTrackIds.size > 0) {
        try {
          cascadedTrackCount = await cascadeDeleteTracks(Array.from(aggregateTrackIds));
          if (cascadedTrackCount > 0) {
            const noun = cascadedTrackCount === 1 ? 'track' : 'tracks';
            const scope = normalizedIds.length === 1 ? 'this recording' : 'these recordings';
            void message.success(`Also removed ${cascadedTrackCount} ${noun} from ${scope}`);
          }
        } catch (error) {
          cascadedTrackCount = 0;
          if (process.env.NODE_ENV === 'development') {
            console.warn('[yourLibrary] Failed to cascade recording track removals', normalizedIds, error);
          }
          void message.warning(
            'Removed the recording, but we could not remove its tracks. Please try again in a bit.'
          );
        }
      }
    }

    return {
      requestedRecordingIds: normalizedIds,
      removed: removedCount,
      skipped: skippedCount,
      cascadedTrackCount,
      trackAutomationAttempted,
    };
  }
);

type RecordingDetailCache = Map<string, any>;

const collectTrackIds = (value: unknown, accumulator: Set<string>) => {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      collectTrackIds(item, accumulator);
    }
    return;
  }

  if (typeof value === 'object') {
    const candidate = value as Record<string, unknown>;
    collectTrackIds(candidate.spotifyTrackId ?? candidate.spotify_track_id, accumulator);
    collectTrackIds(candidate.trackId ?? candidate.track_id, accumulator);
    collectTrackIds(candidate.spotifyUri ?? candidate.spotify_uri, accumulator);
    collectTrackIds(candidate.uri, accumulator);
    collectTrackIds(candidate.id, accumulator);
    return;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const text = typeof value === 'number' ? String(value) : value.trim();
    if (!text) {
      return;
    }

    const normalized = normalizeSpotifyTrackId(text);
    if (normalized) {
      accumulator.add(normalized);
      return;
    }

    const candidates = text.match(/[0-9A-Za-z]{22}/g);
    if (candidates) {
      for (const match of candidates) {
        accumulator.add(match);
      }
    }
  }
};

const extractRecordingTrackIds = (detail: any): string[] => {
  const unique = new Set<string>();
  if (!detail || typeof detail !== 'object') {
    return [];
  }

  const recording = detail.recording && typeof detail.recording === 'object' ? detail.recording : undefined;
  const raw = detail.raw && typeof detail.raw === 'object' ? detail.raw : undefined;

  if (recording) {
    collectTrackIds((recording as Record<string, unknown>).trackIds, unique);
    collectTrackIds((recording as Record<string, unknown>).track_ids, unique);
    collectTrackIds((recording as Record<string, unknown>).trackUris, unique);
    collectTrackIds((recording as Record<string, unknown>).track_uris, unique);
    collectTrackIds((recording as Record<string, unknown>).tracks, unique);
  }

  collectTrackIds(detail.tracks, unique);

  if (raw) {
    collectTrackIds((raw as Record<string, unknown>).trackIds, unique);
    collectTrackIds((raw as Record<string, unknown>).track_ids, unique);
    collectTrackIds((raw as Record<string, unknown>).tracks, unique);
  }

  return Array.from(unique);
};

const getRecordingDetail = async (
  recordingId: string,
  cache?: RecordingDetailCache
): Promise<any | null> => {
  if (!recordingId) {
    return null;
  }

  if (cache && cache.has(recordingId)) {
    return cache.get(recordingId) ?? null;
  }

  try {
    const detail = await recordingsService.fetchRecordingDetail(recordingId);
    if (cache) {
      cache.set(recordingId, detail ?? null);
    }
    return detail ?? null;
  } catch (error) {
    if (cache) {
      cache.set(recordingId, null);
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn('[yourLibrary] Failed to load recording detail', recordingId, error);
    }
    return null;
  }
};

async function resolveCanonicalRecordingWorkId(
  recordingId: string,
  cache?: RecordingDetailCache
): Promise<string | undefined> {
  if (!recordingId) {
    return undefined;
  }

  const detail = await getRecordingDetail(recordingId, cache);
  if (!detail) {
    return undefined;
  }

  const works = Array.isArray(detail.works) ? detail.works : [];
  let bestId: string | undefined;
  let bestConfidence = Number.NEGATIVE_INFINITY;

  for (const work of works) {
    if (!work || typeof work !== 'object') {
      continue;
    }

    const id = stringOrUndefined((work as Record<string, unknown>).id);
    if (!id) {
      continue;
    }

    const confidence = numberOrUndefined((work as Record<string, unknown>).confidence);
    if (typeof confidence === 'number') {
      if (!bestId || confidence > bestConfidence) {
        bestId = id;
        bestConfidence = confidence;
      }
      continue;
    }

    if (!bestId) {
      bestId = id;
    }
  }

  return bestId;
}

const resolveRecordingTrackIds = async (
  recordingId: string,
  cache?: RecordingDetailCache
): Promise<string[]> => {
  if (!recordingId) {
    return [];
  }

  const detail = await getRecordingDetail(recordingId, cache);
  if (!detail) {
    return [];
  }

  return extractRecordingTrackIds(detail);
};

const toYearLabel = (value: unknown): string | undefined => {
  const numeric = numberOrUndefined(value);
  if (typeof numeric === 'number') {
    return Math.trunc(numeric).toString();
  }
  const text = stringOrUndefined(value);
  if (!text) return undefined;
  const match = text.match(/(\d{4})/);
  return match ? match[1] : text;
};

const resolveDurationLabelFromRecording = (recording: Record<string, any>): string | undefined => {
  const direct =
    stringOrUndefined(recording.durationLabel) ??
    stringOrUndefined(recording.duration_label) ??
    stringOrUndefined(recording.durationDisplay) ??
    stringOrUndefined(recording.duration_display);
  if (direct) return direct;

  const stats = recording.stats;
  if (stats && typeof stats === 'object') {
    const statsLabel =
      stringOrUndefined((stats as Record<string, any>).durationLabel) ??
      stringOrUndefined((stats as Record<string, any>).duration_label);
    if (statsLabel) return statsLabel;
  }

  const durationCandidates = [
    recording.durationMsTotal,
    recording.duration_ms_total,
    recording.durationTotalMs,
    recording.duration_total_ms,
    recording.totalDurationMs,
    recording.total_duration_ms,
    stats && typeof stats === 'object' ? (stats as Record<string, any>).durationMsTotal : undefined,
    stats && typeof stats === 'object' ? (stats as Record<string, any>).duration_ms_total : undefined,
  ];

  for (const candidate of durationCandidates) {
    const durationMs = numberOrUndefined(candidate);
    if (typeof durationMs === 'number' && durationMs > 0) {
      const minutes = Math.max(1, Math.round(durationMs / 60000));
      return `${minutes} min`;
    }
  }

  return undefined;
};

const extractRecordingName = (recording: Record<string, any>): string | undefined => {
  return (
    stringOrUndefined(recording.recordingName) ??
    stringOrUndefined(recording.recording_name) ??
    stringOrUndefined(recording.name) ??
    stringOrUndefined(recording.title) ??
    stringOrUndefined(recording.displayName) ??
    stringOrUndefined(recording.display_name)
  );
};

const extractPerformerName = (recording: Record<string, any>): string | undefined => {
  const direct =
    stringOrUndefined(recording.performerSummary) ??
    stringOrUndefined(recording.performer_summary) ??
    stringOrUndefined(recording.performerName) ??
    stringOrUndefined(recording.performer_name) ??
    stringOrUndefined(recording.primaryArtist) ??
    stringOrUndefined(recording.primary_artist);
  if (direct) return direct;

  const performers = recording.performers ?? recording.artists;
  if (Array.isArray(performers)) {
    for (const entry of performers) {
      if (!entry) continue;
      if (typeof entry === 'string') {
        const trimmed = stringOrUndefined(entry);
        if (trimmed) return trimmed;
      } else if (typeof entry === 'object') {
        const name = stringOrUndefined((entry as Record<string, any>).name);
        if (name) return name;
      }
    }
  }
  return undefined;
};

const extractAlbumIdFromRecording = (recording: Record<string, any>, recordingId: string): string => {
  return (
    stringOrUndefined(recording.albumId) ??
    stringOrUndefined(recording.album_id) ??
    stringOrUndefined(recording.spotifyAlbumId) ??
    stringOrUndefined(recording.spotify_album_id) ??
    (typeof recording.album === 'object'
      ? stringOrUndefined(
          (recording.album as Record<string, any>).id ??
            (recording.album as Record<string, any>).spotifyAlbumId ??
            (recording.album as Record<string, any>).spotify_album_id
        )
      : undefined) ??
    `cmf-album-${recordingId}`
  );
};

const extractAlbumNameFromRecording = (recording: Record<string, any>): string | undefined => {
  return (
    stringOrUndefined(recording.albumName) ??
    stringOrUndefined(recording.album_name) ??
    (typeof recording.album === 'object'
      ? stringOrUndefined(
          (recording.album as Record<string, any>).name ??
            (recording.album as Record<string, any>).title ??
            (recording.album as Record<string, any>).displayName
        )
      : undefined)
  );
};

const extractAlbumImageUrlFromRecording = (recording: Record<string, any>): string | undefined => {
  const direct =
    stringOrUndefined(recording.albumImageUrl) ??
    stringOrUndefined(recording.album_image_url) ??
    stringOrUndefined(recording.imageUrl) ??
    stringOrUndefined(recording.image_url) ??
    stringOrUndefined(recording.coverUrl) ??
    stringOrUndefined(recording.cover_url);
  if (direct) return direct;

  const album = recording.album;
  if (album && typeof album === 'object') {
    const albumDirect =
      stringOrUndefined((album as Record<string, any>).imageUrl) ??
      stringOrUndefined((album as Record<string, any>).image_url) ??
      stringOrUndefined((album as Record<string, any>).coverUrl) ??
      stringOrUndefined((album as Record<string, any>).cover_url);
    if (albumDirect) return albumDirect;

    const images = (album as Record<string, any>).images;
    if (Array.isArray(images)) {
      for (const entry of images) {
        if (!entry || typeof entry !== 'object') continue;
        const imageUrl =
          stringOrUndefined((entry as Record<string, any>).url) ??
          stringOrUndefined((entry as Record<string, any>).src);
        if (imageUrl) return imageUrl;
      }
    }
  }
  return undefined;
};

const extractRecordingUri = (recording: Record<string, any>, recordingId: string): string => {
  return (
    stringOrUndefined(recording.uri) ??
    stringOrUndefined(recording.spotifyUri) ??
    stringOrUndefined(recording.spotify_uri) ??
    stringOrUndefined(recording.cmfUri) ??
    `cmf:recording:${recordingId}`
  );
};

const extractComposerNameFromRecording = (recording: Record<string, any>): string | undefined => {
  const direct =
    stringOrUndefined(recording.composerName) ??
    stringOrUndefined(recording.composer_name) ??
    stringOrUndefined(recording.composer);
  if (direct) return direct;

  const composer = recording.composer;
  if (composer && typeof composer === 'object') {
    return (
      stringOrUndefined((composer as Record<string, any>).name) ??
      stringOrUndefined((composer as Record<string, any>).displayName) ??
      stringOrUndefined((composer as Record<string, any>).display_name)
    );
  }

  const works = recording.works;
  if (Array.isArray(works)) {
    for (const entry of works) {
      if (!entry || typeof entry !== 'object') continue;
      const nestedComposer = (entry as Record<string, any>).composer;
      if (nestedComposer && typeof nestedComposer === 'object') {
        const name =
          stringOrUndefined((nestedComposer as Record<string, any>).name) ??
          stringOrUndefined((nestedComposer as Record<string, any>).displayName) ??
          stringOrUndefined((nestedComposer as Record<string, any>).display_name);
        if (name) return name;
      }
    }
  }
  return undefined;
};

const buildLibraryRecordingFromCatalog = (
  recording: Record<string, any> | null,
  recordingId: string,
  savedAt: string,
  options: { workId?: string; workData?: Record<string, any> | null } = {}
): LibraryRecording => {
  const recordingData = recording ?? {};
  const baseName = recording ? extractRecordingName(recordingData) : undefined;
  const performer = recording ? extractPerformerName(recordingData) : undefined;
  const albumId = recording ? extractAlbumIdFromRecording(recordingData, recordingId) : `cmf-album-${recordingId}`;
  const albumName = recording ? extractAlbumNameFromRecording(recordingData) ?? UNKNOWN_ALBUM : UNKNOWN_ALBUM;
  const albumImageUrl = recording ? extractAlbumImageUrlFromRecording(recordingData) : undefined;
  const composerFromRecording = recording ? extractComposerNameFromRecording(recordingData) : undefined;

  const libraryRecording: LibraryRecording = {
    id: recordingId,
    uri: recording ? extractRecordingUri(recordingData, recordingId) : `cmf:recording:${recordingId}`,
    type: 'recording',
    name: baseName ?? recordingId ?? UNKNOWN_RECORDING,
    performerName: performer ?? UNKNOWN_PERFORMER,
    workName: UNKNOWN_WORK,
    composerName: composerFromRecording ?? UNKNOWN_COMPOSER,
    albumId,
    albumName,
    albumImageUrl: albumImageUrl ?? undefined,
    imageUrl: recording ? stringOrUndefined(recordingData.imageUrl ?? recordingData.image_url) ?? undefined : undefined,
    durationLabel: recording ? resolveDurationLabelFromRecording(recordingData) : undefined,
    year: recording ? toYearLabel(recordingData.releaseYear ?? recordingData.year) : undefined,
    workId: options.workId,
    savedAt,
    labels: [],
  };

  if (!libraryRecording.imageUrl && libraryRecording.albumImageUrl) {
    libraryRecording.imageUrl = libraryRecording.albumImageUrl;
  }

  if (recording) {
    const trackCollection = Array.isArray((recordingData as Record<string, any>).tracks)
      ? (recordingData as { tracks: any[] }).tracks
      : [];

    if (trackCollection.length) {
      const primaryTrack = trackCollection.find((track) => {
        if (!track || typeof track !== 'object') return false;
        return (
          stringOrUndefined((track as any).spotifyTrackId) ||
          stringOrUndefined((track as any).spotify_track_id) ||
          stringOrUndefined((track as any).spotifyUri) ||
          stringOrUndefined((track as any).spotify_uri) ||
          stringOrUndefined((track as any).uri) ||
          stringOrUndefined((track as any).id)
        );
      }) as Record<string, any> | undefined;

      if (primaryTrack) {
        const trackIdCandidate =
          stringOrUndefined(primaryTrack.spotifyTrackId) ??
          stringOrUndefined(primaryTrack.spotify_track_id) ??
          stringOrUndefined(primaryTrack.id);

        let spotifyTrackId =
          trackIdCandidate && /^[0-9A-Za-z]{22}$/.test(trackIdCandidate) ? trackIdCandidate : undefined;

        const resolvedTrackUri =
          stringOrUndefined(primaryTrack.spotifyUri) ??
          stringOrUndefined(primaryTrack.spotify_uri) ??
          stringOrUndefined(primaryTrack.uri) ??
          (spotifyTrackId ? `spotify:track:${spotifyTrackId}` : undefined);

        if (!spotifyTrackId && resolvedTrackUri) {
          const match = resolvedTrackUri.match(/^spotify:track:([0-9A-Za-z]{22})$/i);
          if (match) {
            spotifyTrackId = match[1];
          }
        }

        if (spotifyTrackId && !libraryRecording.spotifyTrackId) {
          libraryRecording.spotifyTrackId = spotifyTrackId;
        }
        if (resolvedTrackUri && !libraryRecording.spotifyUri) {
          libraryRecording.spotifyUri = resolvedTrackUri;
        }
        if (
          resolvedTrackUri &&
          (!libraryRecording.uri || libraryRecording.uri.startsWith('cmf:recording:'))
        ) {
          libraryRecording.uri = resolvedTrackUri;
        }
      }
    }
  }

  if (recording) {
    const derivedWorkId = libraryRecording.workId ?? derivePrimaryWorkId(recordingData);
    if (derivedWorkId) {
      libraryRecording.workId = derivedWorkId;
    }

    const recordingWorkName =
      stringOrUndefined(recordingData.workName) ?? stringOrUndefined(recordingData.work_name);
    if (recordingWorkName) {
      libraryRecording.workName = recordingWorkName;
    }
  }

  if (options.workData) {
    const resolvedName = extractName(options.workData);
    if (resolvedName) {
      libraryRecording.workName = resolvedName;
    }
    const resolvedComposerName = extractComposerName(options.workData);
    if (resolvedComposerName) {
      libraryRecording.composerName = resolvedComposerName;
    }
    const composerId = extractComposerId(options.workData);
    if (composerId) {
      libraryRecording.composerId = composerId;
    }
    const resolvedWorkId =
      stringOrUndefined(options.workData.id) ??
      stringOrUndefined(options.workData.workId) ??
      stringOrUndefined(options.workData.slug);
    if (resolvedWorkId) {
      libraryRecording.workId = libraryRecording.workId ?? resolvedWorkId;
    }
  }

  if (!libraryRecording.workName || libraryRecording.workName === UNKNOWN_WORK) {
    libraryRecording.workName = libraryRecording.name || recordingId || UNKNOWN_WORK;
  }

  if (!libraryRecording.composerName) {
    libraryRecording.composerName = UNKNOWN_COMPOSER;
  }

  return libraryRecording;
};

const initialState: YourLibraryState = {
  search: '',
  myAlbums: [],
  view: 'LIST',
  filter: 'ALL',
  composerSortOrder: 'relevance',
  myArtists: [],
  myPlaylists: [],
  myComposers: [],
  myWorks: [],
  myRecordings: [],
  recordingsStatus: 'idle',
  recordingsFetchInFlight: false,
  artistsFetchInFlight: false,
  artistsStatus: 'idle',
  albumsFetchInFlight: false,
  albumsStatus: 'idle',
  albumsSyncStatus: 'idle',
  worksStatus: 'idle',
  worksFetchInFlight: false,
  playlistsStatus: 'idle',
  playlistsFetchInFlight: false,
  albumsError: null,
  orderBy: 'default',
  albumsPageCursor: null,
  albumsPageSessionId: null,
  composersSourceSignature: null,
  composersSourceUserId: null,
  lastFetchedAt: {
    albums: null,
    artists: null,
    playlists: null,
    composers: null,
    works: null,
    recordings: null,
  },
  librarySyncSettings: {
    status: 'idle',
    updateStatus: 'idle',
    value: createDefaultLibrarySyncSettings(),
    error: null,
    lastFetchedAt: null,
    pendingUpdate: null,
    isSyncingAlbumTracks: false,
    isSyncingAlbumRecordings: false,
    isSyncingRecordingWorks: false,
    isSyncingAlbumRecordingsAndWorks: false,
    isSyncingTrackRecordings: false,
    recordingsAndWorksJob: null,
    recordingsAndWorksJobId: null,
  },
  importSyncSelections: {
    importLibrary: true,
    syncRecordings: true,
    syncTrackRecordings: true,
  },
  importStatus: 'idle',
  importError: null,
  lastImportStartedAt: null,
  lastImportRequiresAlbumSync: null,
  lastImportRequiresTrackSync: null,
  lastImportCompletedAt: null,
  lastRecordingsAndWorksSyncStartedAt: null,
};

const normalizeSpotifyArtistId = (artist: Artist | null | undefined): string | undefined => {
  if (!artist) return undefined;
  if (typeof artist.id === 'string' && artist.id.trim()) {
    return artist.id.trim();
  }
  if (typeof artist.uri === 'string') {
    const match = artist.uri.match(/^spotify:artist:(.+)$/i);
    if (match?.[1]) {
      const candidate = match[1].trim();
      return candidate.length ? candidate : undefined;
    }
  }
  return undefined;
};

const buildArtistSnapshotSignature = (userId: string | null, artists: Artist[]): string => {
  const base = userId ?? 'anonymous';
  if (!artists.length) {
    return `${base}:0`;
  }
  const ids = artists
    .map((artist) => normalizeSpotifyArtistId(artist) ?? '')
    .join('|');
  return `${base}:${artists.length}:${ids}`;
};

const syncFavoriteArtistsWithBackend = async (artists: Artist[]): Promise<void> => {
  try {
    const spotifyIds = new Set<string>();
    for (const artist of artists) {
      const id = normalizeSpotifyArtistId(artist);
      if (id) {
        spotifyIds.add(id);
      }
    }

    const favorites = await favoriteArtistService.getFavoriteArtists();
    const favoriteIds = new Map<string, string>();
    for (const favorite of favorites) {
      const id =
        typeof favorite?.spotifyArtistId === 'string' && favorite.spotifyArtistId.trim().length
          ? favorite.spotifyArtistId.trim()
          : typeof favorite?.id === 'string' && favorite.id.trim().length
          ? favorite.id.trim()
          : null;
      if (!id) continue;
      favoriteIds.set(id, favorite.spotifyArtistId);
    }

    const toAdd: string[] = [];
    for (const spotifyId of spotifyIds) {
      if (!favoriteIds.has(spotifyId)) {
        toAdd.push(spotifyId);
      }
    }

    const toRemove: string[] = [];
    for (const [favoriteId, canonicalId] of favoriteIds.entries()) {
      if (!spotifyIds.has(favoriteId)) {
        toRemove.push(canonicalId ?? favoriteId);
      }
    }

    if (!toAdd.length && !toRemove.length) {
      return;
    }

    for (const spotifyId of toAdd) {
      try {
        await favoriteArtistService.saveFavoriteArtist(spotifyId);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[yourLibrary] Failed to upsert favorite artist', spotifyId, error);
        }
      }
    }

    for (const spotifyId of toRemove) {
      try {
        await favoriteArtistService.removeFavoriteArtist(spotifyId);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[yourLibrary] Failed to remove favorite artist', spotifyId, error);
        }
      }
    }
  } catch (error: any) {
    if (error?.response?.status === 401) {
      return;
    }
    if (process.env.NODE_ENV === 'development') {
      console.error('[yourLibrary] Failed to sync favorite artists', error);
    }
  }
};

const resolveComposerSyncCandidates = (
  composer: LibraryComposer | null | undefined
): { primary: string; candidates: string[]; savedAt?: string } | null => {
  if (!composer) return null;
  const candidates = new Set<string>();
  if (typeof composer.id === 'string' && composer.id.trim().length) {
    candidates.add(composer.id.trim());
  }
  if (typeof composer.spotifyArtistId === 'string' && composer.spotifyArtistId.trim().length) {
    candidates.add(composer.spotifyArtistId.trim());
  }
  if (!candidates.size) {
    return null;
  }
  const primary = Array.from(candidates.values())[0]!;
  return { primary, candidates: Array.from(candidates.values()), savedAt: composer.savedAt };
};

const syncFavoriteComposersWithBackend = async (composers: LibraryComposer[]): Promise<void> => {
  try {
    const favorites = await favoriteComposerService.getFavoriteComposers();
    const existingIds = new Set<string>();
    for (const favorite of favorites) {
      if (typeof favorite?.composerId === 'string' && favorite.composerId.trim().length) {
        existingIds.add(favorite.composerId.trim());
      }
    }

    const toAdd = new Map<string, string | undefined>();
    for (const composer of composers) {
      const normalized = resolveComposerSyncCandidates(composer);
      if (!normalized) continue;
      let alreadyFavorited = false;
      for (const candidate of normalized.candidates) {
        if (existingIds.has(candidate)) {
          alreadyFavorited = true;
        }
      }
      if (!alreadyFavorited && !toAdd.has(normalized.primary)) {
        toAdd.set(normalized.primary, normalized.savedAt);
        existingIds.add(normalized.primary);
      }
    }

    if (!toAdd.size) {
      return;
    }

    for (const [composerId, savedAt] of toAdd.entries()) {
      try {
        await favoriteComposerService.saveFavoriteComposer(composerId, { savedAt });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[yourLibrary] Failed to upsert favorite composer', composerId, error);
        }
      }
    }
  } catch (error: any) {
    if (error?.response?.status === 401) {
      return;
    }
    if (process.env.NODE_ENV === 'development') {
      console.error('[yourLibrary] Failed to sync favorite composers', error);
    }
  }
};

export const deriveMyComposers = createAsyncThunk<
  { composers: LibraryComposer[]; signature: string; userId: string | null },
  void,
  { state: RootState }
>('yourLibrary/deriveMyComposers', async (_, { getState }) => {
  const LOG_PREFIX = '[yourLibrary:deriveMyComposers]';
  const overallStart = process.env.NODE_ENV === 'development' ? Date.now() : 0;
  const state = getState();
  const artists = state.yourLibrary.myArtists ?? [];
  const userId = state.auth?.user?.id ?? null;
  const signature = buildArtistSnapshotSignature(userId, artists);

  let knownComposerIds = new Set<string>(getKnownComposerIds());
  let loadDuration = 0;
  if (knownComposerIds.size === 0) {
    const start = Date.now();
    if (process.env.NODE_ENV === 'development') {
      console.info(`${LOG_PREFIX} registry cold, loading catalog composers`);
    }
    try {
      await loadCatalogComposerIds();
    } catch {
      // Ignore errors; registry will log in development environments.
    }
    knownComposerIds = new Set<string>(getKnownComposerIds());
    loadDuration = Date.now() - start;
    if (process.env.NODE_ENV === 'development') {
      console.info(
        `${LOG_PREFIX} registry load completed`,
        { durationMs: Date.now() - start, size: knownComposerIds.size }
      );
    }
  } else if (process.env.NODE_ENV === 'development') {
    console.info(`${LOG_PREFIX} registry already warm`, { size: knownComposerIds.size });
  }

  if (!artists.length || knownComposerIds.size === 0) {
    return { composers: [], signature, userId };
  }

  const seen = new Set<string>();
  const composers: LibraryComposer[] = [];
  const hydrateStart = process.env.NODE_ENV === 'development' ? Date.now() : 0;

  for (const artist of artists) {
    if (!artist) continue;
    const spotifyId = normalizeSpotifyArtistId(artist);
    if (!spotifyId) continue;
    if (!knownComposerIds.has(spotifyId)) continue;
    if (seen.has(spotifyId)) continue;
    seen.add(spotifyId);

    const catalogId = getCatalogIdForSpotifyId(spotifyId);
    const name =
      typeof artist.name === 'string' && artist.name.trim() ? artist.name.trim() : UNKNOWN_COMPOSER;
    const uri =
      typeof artist.uri === 'string' && artist.uri.trim()
        ? artist.uri.trim()
        : `spotify:artist:${spotifyId}`;
    const imageCandidate =
      Array.isArray(artist.images) && artist.images.length
        ? artist.images.find((image) => typeof image?.url === 'string' && image.url.trim())
        : undefined;

    const composer: LibraryComposer = {
      id: catalogId ?? spotifyId,
      uri,
      type: 'composer',
      name,
      spotifyArtistId: spotifyId,
    };

    if (imageCandidate?.url) {
      composer.imageUrl = imageCandidate.url.trim();
    }

    composers.push(composer);
  }

  if (process.env.NODE_ENV === 'development') {
    const hydrateDuration = Date.now() - hydrateStart;
    const totalDuration = Date.now() - overallStart;
    console.info(`${LOG_PREFIX} hydration complete`, {
      loadDurationMs: loadDuration,
      hydrateDurationMs: hydrateDuration,
      totalDurationMs: totalDuration,
      artistCount: artists.length,
      matchedCount: composers.length
    });
  }

  await syncFavoriteComposersWithBackend(composers);

  return { composers, signature, userId };
});

export const fetchMyPlaylists = createAsyncThunk(
  'yourLibrary/fetchMyPlaylists',
  async (_, thunkAPI) => {
    const limit = 50;
    let offset = 0;
    const playlists: Playlist[] = [];
    const lastPlayedMap = new Map<string, number>();
    const state = thunkAPI.getState() as RootState;
    // Match backendClient's getSpotifyUserId ordering: profile.user.id first, then auth.user.id.
    const userId = state.profile.user?.id ?? state.auth.user?.id ?? null;

    if (userId) {
      try {
        const { data } = await backendClient.get<{
          items?: Array<{ playlistId: string; lastPlayedAt: string }>;
        }>('/library/playlists/last-played', {
          headers: {
            'X-User-Id': userId
          }
        });
        const items = Array.isArray(data.items) ? data.items : [];
        for (const entry of items) {
          if (!entry || typeof entry.playlistId !== 'string' || typeof entry.lastPlayedAt !== 'string') {
            continue;
          }
          const ts = Date.parse(entry.lastPlayedAt);
          if (Number.isFinite(ts)) {
            lastPlayedMap.set(entry.playlistId, ts);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] failed to load playlist last-played data', error);
        }
      }
    }

    // Loop through every page Spotify exposes for the user's playlists.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { data } = await playlistService.getMyPlaylists({ limit, offset });
      const pageItems = data.items ?? [];
      if (offset === 0) {
        console.log(
          'plaaay:spotify:first5',
          pageItems.slice(0, 5).map((p) => ({ id: p?.id, name: p?.name }))
        );
      }
      if (pageItems.length) {
        playlists.push(...pageItems);
        thunkAPI.dispatch(yourLibraryActions.upsertMyPlaylists(pageItems));
      }
      if (!data.next || pageItems.length === 0) {
        break;
      }
      offset += limit;
    }

    const sorted = playlists.slice().sort((a, b) => {
      const aId = typeof a?.id === 'string' ? a.id : '';
      const bId = typeof b?.id === 'string' ? b.id : '';
      const aTs = aId ? lastPlayedMap.get(aId) : undefined;
      const bTs = bId ? lastPlayedMap.get(bId) : undefined;
      if (typeof aTs === 'number' && typeof bTs === 'number') {
        return bTs - aTs;
      }
      if (typeof aTs === 'number') return -1;
      if (typeof bTs === 'number') return 1;
      return 0;
    });

    console.log(
      'plaaay:sorted:first5',
      sorted.slice(0, 5).map((p) => ({ id: p?.id, name: p?.name }))
    );

    return sorted;
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
    const { playlistsStatus, lastFetchedAt, myPlaylists } = state.yourLibrary;
    const stale =
      !lastFetchedAt.playlists || Date.now() - lastFetchedAt.playlists > YOUR_LIBRARY_TTL_MS;
      // Skip if a fetch is already in flight or we have fresh data.
      return playlistsStatus !== 'loading' && (stale || myPlaylists.length === 0);
    },
  }
);

type SyncMyAlbumsArgs = {
  userId: string;
  accessToken: string;
  pageLimit?: number;
};

type SyncMyAlbumsResult = {
  totalFetched: number;
  pagesFetched: number;
  overlap: boolean;
  nextCursor: string | null;
  sessionId: string | null;
  commit: boolean;
  persistedCount: number;
};

export const syncMyAlbums = createAsyncThunk<SyncMyAlbumsResult, SyncMyAlbumsArgs, { state: RootState }>(
  'yourLibrary/syncMyAlbums',
  async (args, thunkAPI) => {
    let lastResult: SyncMyAlbumsResult | null = null;
    let hasScheduledRefetch = false;
    const maybeScheduleCachedRefetch = (summary: FetchAlbumsPageResponse) => {
      if (hasScheduledRefetch) return;
      const totalPersisted =
        typeof summary.totalPersisted === 'number' && Number.isFinite(summary.totalPersisted)
          ? summary.totalPersisted
          : null;
      const latestAlbumId =
        typeof summary.latestAlbumId === 'string' && summary.latestAlbumId.trim().length > 0
          ? summary.latestAlbumId.trim()
          : null;
      if (totalPersisted === null && !latestAlbumId) {
        return;
      }
      const currentState = thunkAPI.getState() as RootState;
      const currentAlbums = currentState.yourLibrary.myAlbums;
      const currentLatestId = currentAlbums.length > 0 ? currentAlbums[0]?.id ?? null : null;
      const totalMismatch = totalPersisted !== null && totalPersisted !== currentAlbums.length;
      const latestMismatch = latestAlbumId ? currentLatestId !== latestAlbumId : false;
      if (totalMismatch || latestMismatch) {
        hasScheduledRefetch = true;
        thunkAPI.dispatch(fetchMyAlbums());
      }
    };
    try {
      const userId = args.userId.trim();
      const accessToken = args.accessToken.trim();
      console.info('[extracall] syncMyAlbums start', {
        userId,
        hasToken: accessToken.length > 0,
        pageLimit: typeof args.pageLimit === 'number' ? args.pageLimit : null
      });
      if (!userId || !accessToken) {
        lastResult = {
          totalFetched: 0,
          pagesFetched: 0,
          overlap: true,
          nextCursor: null,
          sessionId: null,
          commit: true,
          persistedCount: 0
        };
        return lastResult;
      }

      const maxPages =
        typeof args.pageLimit === 'number' && args.pageLimit > 0
          ? Math.floor(args.pageLimit)
          : undefined;

      let nextCursor: string | null = null;
      let overlap = false;
      let totalFetched = 0;
      let pagesFetched = 0;
      let sessionId: string | null = null;
      let commit = false;
      let persistedCount = 0;

      thunkAPI.dispatch(yourLibraryActions.setAlbumsPageCursor(null));
      thunkAPI.dispatch(yourLibraryActions.setAlbumsPageSessionId(null));

      if (DISABLE_ALBUM_PAGE_FETCH) {
        lastResult = {
          totalFetched: 0,
          pagesFetched: 0,
          overlap: true,
          nextCursor: null,
          sessionId: null,
          commit: true,
          persistedCount: 0
        };
        return lastResult;
      }

      while ((maxPages === undefined || pagesFetched < maxPages) && !commit && !overlap) {
        const previousCursor = nextCursor;
        const response = await libraryAlbumsService.fetchAlbumsPage({
          spotifyUserId: userId,
          accessToken,
          after: previousCursor ?? undefined,
          sessionId: sessionId ?? undefined
        });

        pagesFetched += 1;
        totalFetched += response.totalFetched;
        persistedCount += response.persistedCount;
        maybeScheduleCachedRefetch(response);

        if (response.sessionId) {
          sessionId = response.sessionId;
        }

        if (response.albums.length > 0) {
          thunkAPI.dispatch(yourLibraryActions.upsertMyAlbums(response.albums));
        }

        const nextCursorFromResponse = response.nextCursor;
        overlap = response.overlap;
        commit = response.commit;

        nextCursor = nextCursorFromResponse;
        thunkAPI.dispatch(yourLibraryActions.setAlbumsPageCursor(nextCursor));
        thunkAPI.dispatch(
          yourLibraryActions.setAlbumsPageSessionId(
            response.commit ? null : sessionId ?? response.sessionId ?? null
          )
        );

        if (
          response.commit ||
          response.overlap ||
          nextCursorFromResponse === null ||
          nextCursorFromResponse === previousCursor
        ) {
          commit = response.commit || nextCursorFromResponse === null;
          break;
        }
      }

      lastResult = {
        totalFetched,
        pagesFetched,
        overlap,
        nextCursor,
        sessionId: commit ? null : sessionId,
        commit,
        persistedCount
      };
      return lastResult;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] Failed to fetch incremental saved albums', error);
      }
      throw error;
    } finally {
      console.info('[extracall] syncMyAlbums end', lastResult);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return state.yourLibrary.albumsSyncStatus !== 'pending';
    }
  }
);

export const fetchMyAlbums = createAsyncThunk<Album[], void, { state: RootState }>(
  'yourLibrary/fetchMyAlbums',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userId = state.auth.user?.id?.trim();

    if (!userId) {
      return [];
    }

    try {
      const cachedAlbums = await libraryAlbumsService.fetchCachedAlbums(userId);

      const tokenCandidates: Array<string | undefined> = [
        state.auth.token,
        getFromLocalStorageWithExpiry('access_token') as string | undefined,
        getFromLocalStorageWithExpiry('public_access_token') as string | undefined
      ];

      const accessToken = tokenCandidates.find(
        (token) => typeof token === 'string' && token.trim().length > 0
      );

      if (accessToken) {
        thunkAPI.dispatch(
          syncMyAlbums({
            userId,
            accessToken: accessToken.trim()
          })
        );
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Skipping album sync due to missing access token');
      }

      return cachedAlbums;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] Failed to load cached albums', error);
      }
      throw error;
    }
  }
);

export const fetchMyArtists = createAsyncThunk<Artist[], void, { state: RootState; dispatch: AppDispatch }>(
  'yourLibrary/fetchMyArtists',
  async (_, thunkAPI) => {
    const LOG_PREFIX = '[yourLibrary:fetchMyArtists]';
    const overallStart = process.env.NODE_ENV === 'development' ? Date.now() : 0;
    const limit = 50;
    let after: string | undefined;
    const artists: Artist[] = [];
    let deriveInFlight = false;

    const maybeTriggerDerive = () => {
      if (LEGACY_COMPOSER_SYNC_ENABLED || deriveInFlight) {
        return;
      }
      const state = thunkAPI.getState() as RootState;
      const userId = state.auth.user?.id ?? null;
      const signature = buildArtistSnapshotSignature(userId, artists);
      const existingSignature = state.yourLibrary.composersSourceSignature;
      const existingUserId = state.yourLibrary.composersSourceUserId;
      const existingCount = state.yourLibrary.myComposers.length;
      if (
        existingSignature !== signature ||
        existingUserId !== userId ||
        existingCount === 0
      ) {
        deriveInFlight = true;
        void thunkAPI
          .dispatch(deriveMyComposers())
          .finally(() => {
            deriveInFlight = false;
          });
      }
    };

    // Follow cursor-based pagination until Spotify stops returning new items.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const pageStart = process.env.NODE_ENV === 'development' ? Date.now() : 0;
      const { data } = await userService.fetchFollowedArtists({ limit, after });
      const pageArtists = data.artists.items ?? [];
      if (pageArtists.length) {
        artists.push(...pageArtists);
        thunkAPI.dispatch(yourLibraryActions.upsertMyArtists(pageArtists));
        maybeTriggerDerive();
      }
      if (process.env.NODE_ENV === 'development') {
        console.info(`${LOG_PREFIX} fetched page`, {
          afterCursor: after ?? null,
          nextCursor: data.artists.cursors?.after ?? null,
          pageCount: pageArtists.length,
          accumulated: artists.length,
          durationMs: Date.now() - pageStart
        });
      }
      const nextCursor = data.artists.cursors?.after ?? undefined;
      if (!data.artists.next || !nextCursor || nextCursor === after || pageArtists.length === 0) {
        break;
      }
      after = nextCursor;
    }

    if (process.env.NODE_ENV === 'development') {
      console.info(`${LOG_PREFIX} completed`, {
        total: artists.length,
      totalDurationMs: Date.now() - overallStart
    });
    }

    maybeTriggerDerive();
    await syncFavoriteArtistsWithBackend(artists);

    return artists;
  }
);

export const fetchMyComposers = createAsyncThunk('yourLibrary/fetchMyComposers', async () => {
  const composers = await libraryService.fetchComposers();
  return composers;
});

export const fetchMyWorks = createAsyncThunk<
  LibraryWork[],
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  'yourLibrary/fetchMyWorks',
  async (_, thunkAPI) => {
  const logLabel = '[yourLibrary:fetchMyWorks] total duration';
  const logEnabled = process.env.NODE_ENV === 'development';
  const startTime = logEnabled ? performance.now() : 0;

  try {
    const favorites = await favoriteWorksService.getFavoriteWorks();
    if (!favorites.length) {
      if (logEnabled) {
        const duration = performance.now() - startTime;
        console.log(`${logLabel}: ${duration.toFixed(2)}ms (empty)`);
      }
      return [];
    }

    const uniqueFavorites: Array<{ workId: string; savedAt: string }> = [];
    const seen = new Set<string>();

    for (const favorite of favorites) {
      const rawWorkId = favorite.workId ?? favorite.id;
      const workId = typeof rawWorkId === 'string' ? rawWorkId.trim() : '';
      if (!workId || seen.has(workId)) {
        continue;
      }
      seen.add(workId);

      const savedAt = favorite.savedAt ?? new Date().toISOString();
      uniqueFavorites.push({ workId, savedAt });
    }

    if (!uniqueFavorites.length) {
      if (logEnabled) {
        const duration = performance.now() - startTime;
        console.log(`${logLabel}: ${duration.toFixed(2)}ms (no unique favorites)`);
      }
      return [];
    }

    const workIds = uniqueFavorites.map((favorite) => favorite.workId);
    const metadataById = new Map<string, Record<string, any>>();

    const FIRST_WORK_BATCH_SIZE = 200;
    const firstIds = workIds.slice(0, FIRST_WORK_BATCH_SIZE);
    const remainingIds = workIds.slice(FIRST_WORK_BATCH_SIZE);

    const ingestWorks = (fetchedWorks: any[]) => {
      for (const work of fetchedWorks) {
        if (!work) continue;
        const candidates: Array<string | undefined> = [work.id, (work as any).workId];
        candidates.forEach((candidate) => {
          if (!candidate || typeof candidate !== 'string') return;
          const trimmed = candidate.trim();
          if (trimmed) {
            metadataById.set(trimmed, work as unknown as Record<string, any>);
          }
        });
      }
    };

    // Fetch first batch to enable earlier render
    try {
      const firstWorks = await worksService.fetchWorksByIds(firstIds);
      ingestWorks(firstWorks);

      const firstIdSet = new Set(firstIds);
      const partial = uniqueFavorites
        .filter((favorite) => firstIdSet.has(favorite.workId))
        .map((favorite) => {
          const catalogWork = metadataById.get(favorite.workId);
          if (catalogWork) {
            return buildLibraryWorkFromCatalog(
              catalogWork as Record<string, any>,
              favorite.workId,
              favorite.savedAt
            );
          }
          return buildFallbackLibraryWork(favorite.workId, favorite.savedAt);
        });

      if (partial.length) {
        // best-effort early render
        thunkAPI.dispatch(yourLibraryActions.upsertMyWorks(partial));
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Failed to load initial work metadata', error);
      }
    }

    // Fetch remaining work metadata
    if (remainingIds.length > 0) {
      try {
        const fetchedWorks = await worksService.fetchWorksByIds(remainingIds);
        ingestWorks(fetchedWorks);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to load batch work metadata', error);
        }
      }
    }

    const works = uniqueFavorites.map((favorite) => {
      const catalogWork = metadataById.get(favorite.workId);
      if (catalogWork) {
        return buildLibraryWorkFromCatalog(
          catalogWork as Record<string, any>,
          favorite.workId,
          favorite.savedAt
        );
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Missing batch metadata for favorite work', favorite.workId);
      }

      return buildFallbackLibraryWork(favorite.workId, favorite.savedAt);
    });
    try {
      await thunkAPI.dispatch(persistLibraryCache({ works })).unwrap();
    } catch {
      // ignore cache write failures
    }
    return works;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[yourLibrary] Failed to fetch favorite works', error);
    }
    return [];
  } finally {
    if (logEnabled) {
      const duration = performance.now() - startTime;
      console.log(`${logLabel}: ${duration.toFixed(2)}ms`);
    }
  }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return state.yourLibrary.worksStatus !== 'loading';
    },
  }
);

export const fetchMyRecordings = createAsyncThunk<
  LibraryRecording[],
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  'yourLibrary/fetchMyRecordings',
  async (_, thunkAPI) => {
  if (process.env.NODE_ENV === 'development') {
    const globalObj = typeof window !== 'undefined' ? (window as any) : (globalThis as any);
    const runId = (globalObj.__yourLibraryFetchMyRecordingsRun ?? 0) + 1;
    globalObj.__yourLibraryFetchMyRecordingsRun = runId;
    const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    console.info(
      `[yourLibrary:fetchMyRecordings] start run=${runId} at ${new Date().toISOString()} (${now.toFixed ? now.toFixed(1) : now} ms since load)`
    );
  }
  try {
    const favorites = await favoriteRecordingsService.listFavorites();
    if (process.env.NODE_ENV === 'development') {
      const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      console.info(
        `[yourLibrary:fetchMyRecordings] favorites fetched count=${favorites.length} at ${now.toFixed ? now.toFixed(1) : now} ms`
      );
    }
    if (!favorites.length) {
      return [];
    }

    const uniqueRecordingIds: string[] = [];
    const recordingIdSet = new Set<string>();
    const workIdSet = new Set<string>();

    for (const favorite of favorites) {
      const recordingId =
        stringOrUndefined((favorite as any)?.recordingId) ??
        stringOrUndefined((favorite as any)?.id);
      if (!recordingId) continue;
      if (!recordingIdSet.has(recordingId)) {
        recordingIdSet.add(recordingId);
        uniqueRecordingIds.push(recordingId);
      }
      const favoriteWorkId = stringOrUndefined((favorite as any)?.workId);
      if (favoriteWorkId) {
        workIdSet.add(favoriteWorkId);
      }
    }

    const recordingsById = new Map<string, Record<string, any>>();
    const FIRST_BATCH_SIZE = 300;
    const firstChunk = uniqueRecordingIds.slice(0, FIRST_BATCH_SIZE);
    const remainingIds = uniqueRecordingIds.slice(FIRST_BATCH_SIZE);
    const recordingChunks = [firstChunk, ...chunkArray(remainingIds, RECORDING_FETCH_BATCH_SIZE)].filter(
      (chunk) => chunk.length > 0
    );
    const logBatches = process.env.NODE_ENV === 'development';
    if (logBatches) {
      console.time('[yourLibrary] batch fetch');
    }

    const buildRecordingList = (
      workMetadata: Map<string, Record<string, any>>
    ): LibraryRecording[] => {
      const dedupedRecordingIds = new Set<string>();
      const recordings: LibraryRecording[] = [];

      for (const favorite of favorites) {
        const recordingId =
          stringOrUndefined((favorite as any)?.recordingId) ??
          stringOrUndefined((favorite as any)?.id);
        if (!recordingId || dedupedRecordingIds.has(recordingId)) {
          continue;
        }
        dedupedRecordingIds.add(recordingId);

        if (!recordingsById.has(recordingId)) {
          continue;
        }

        const savedAt = stringOrUndefined((favorite as any)?.savedAt) ?? new Date().toISOString();
        const recordingMetadata = recordingsById.get(recordingId) ?? null;

        let workId =
          stringOrUndefined((favorite as any)?.workId) ??
          (recordingMetadata ? derivePrimaryWorkId(recordingMetadata) : undefined);

        const workData = workId ? workMetadata.get(workId) ?? null : null;

        const recording = buildLibraryRecordingFromCatalog(recordingMetadata, recordingId, savedAt, {
          workId,
          workData: workData ?? undefined,
        });

        if (workId && !recording.workId) {
          recording.workId = workId;
        }

        if (Array.isArray((favorite as any)?.labels) && (favorite as any)?.labels.length > 0) {
          recording.labels = [...((favorite as any)?.labels as string[])];
        } else {
          recording.labels = [];
        }

        if (stringOrUndefined((favorite as any)?.notes)) {
          recording.notes = stringOrUndefined((favorite as any)?.notes);
        }

        recording.savedAt = savedAt;

        recordings.push(recording);
      }

      return recordings;
    };

    const executeChunkBatch = async (chunk: string[], batchIndex: number) => {
      if (!chunk.length) return;
      const start = logBatches ? performance.now() : 0;
      try {
        const batch = await recordingsService.fetchRecordingsBatch(chunk);
        for (const entry of batch) {
          const recordingId =
            stringOrUndefined((entry as any)?.id) ??
            stringOrUndefined((entry as any)?.recordingId);
          if (recordingId) {
            recordingsById.set(recordingId, entry as Record<string, any>);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to load catalog recordings batch', chunk, error);
        }
      } finally {
        if (logBatches) {
          const duration = performance.now() - start;
          console.log('[yourLibrary] batch completed', {
            batchIndex,
            size: chunk.length,
            durationMs: Math.round(duration),
          });
        }
      }
    };

    const totalChunks = recordingChunks.length;
    if (totalChunks === 0) {
      return [];
    }

    // Fetch first chunk up front so the UI can render sooner.
    await executeChunkBatch(recordingChunks[0], 0);
    if (process.env.NODE_ENV === 'development') {
      const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      console.info(
        `[yourLibrary:fetchMyRecordings] first batch executed size=${recordingChunks[0].length} at ${now.toFixed ? now.toFixed(1) : now} ms`
      );
    }

    // Kick a partial render after the first chunk.
    try {
      const initialWorkIds = new Set<string>();
      const firstChunkSet = new Set(recordingChunks[0]);
      firstChunkSet.forEach((recordingId) => {
        const meta = recordingsById.get(recordingId);
        const works = meta?.works;
        if (Array.isArray(works)) {
          for (const entry of works) {
            const associatedWorkId =
              stringOrUndefined((entry as any)?.workId) ??
              stringOrUndefined((entry as any)?.id) ??
              stringOrUndefined((entry as any)?.work_id);
            if (associatedWorkId) {
              initialWorkIds.add(associatedWorkId);
            }
          }
        }
      });

      const initialWorkMetadata = new Map<string, Record<string, any>>();
      if (initialWorkIds.size) {
        try {
          const fetchedWorks = await worksService.fetchWorksByIds(Array.from(initialWorkIds));
          for (const work of fetchedWorks) {
            const candidates: Array<string | undefined> = [work?.id as string | undefined, (work as any)?.workId];
            candidates.forEach((candidate) => {
              if (!candidate || typeof candidate !== 'string') return;
              const trimmed = candidate.trim();
              if (trimmed) {
                initialWorkMetadata.set(trimmed, work as Record<string, any>);
              }
            });
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[yourLibrary] Failed to load initial work metadata', error);
          }
        }
      }

      const partialRecordings = buildRecordingList(initialWorkMetadata);
      if (partialRecordings.length) {
        thunkAPI.dispatch({ type: 'yourLibrary/upsertMyRecordings', payload: partialRecordings });
        thunkAPI.dispatch(yourLibraryActions.setRecordingsStatus('succeeded'));
      }
    } catch {
      // best-effort partial render
    }

    const remainingChunks = recordingChunks.slice(1);
    const remainingTotal = remainingChunks.length;
    let nextIndex = 0;
    const workerCount = Math.min(MAX_CONCURRENT_RECORDING_BATCHES, remainingTotal);

    if (logBatches) {
      console.log('[yourLibrary] batch fetch started', {
        totalChunks: remainingTotal,
        chunkSize: RECORDING_FETCH_BATCH_SIZE,
        workerCount,
      });
    }

    const launchWorker = async (): Promise<void> => {
      while (true) {
        const currentIndex = nextIndex;
        if (currentIndex >= remainingTotal) {
          return;
        }
        nextIndex += 1;
        const chunk = remainingChunks[currentIndex];
        await executeChunkBatch(chunk, currentIndex + 1);
      }
    };

    if (workerCount > 0) {
      const workers = Array.from({ length: workerCount }, () => launchWorker());
      await Promise.all(workers);
    }

    if (logBatches) {
      console.timeEnd('[yourLibrary] batch fetch');
    }

    // Temporarily disable legacy per-recording fallback so we can observe batch behavior end-to-end.
    // const missingRecordingIds = uniqueRecordingIds.filter((recordingId) => !recordingsById.has(recordingId));
    // if (missingRecordingIds.length > 0) {
    //   await Promise.all(
    //     missingRecordingIds.map(async (recordingId) => {
    //       try {
    //         const fallbackRecording = await fetchCatalogRecording(recordingId);
    //         if (fallbackRecording) {
    //           recordingsById.set(recordingId, fallbackRecording);
    //         }
    //       } catch (error) {
    //         if (process.env.NODE_ENV === 'development') {
    //           console.warn('[yourLibrary] Failed to load catalog recording metadata', recordingId, error);
    //         }
    //       }
    //     })
    //   );
    // }

    recordingsById.forEach((metadata) => {
      const works = metadata?.works;
      if (!Array.isArray(works)) {
        return;
      }

      for (let index = 0; index < works.length; index += 1) {
        const entry = works[index];
        const associatedWorkId =
          stringOrUndefined((entry as any)?.workId) ??
          stringOrUndefined((entry as any)?.id) ??
          stringOrUndefined((entry as any)?.work_id);
        if (associatedWorkId) {
          workIdSet.add(associatedWorkId);
        }
      }
    });

    // Fetch work metadata for all IDs we saw.
    const workMetadata = new Map<string, Record<string, any>>();
    if (workIdSet.size) {
      try {
        const fetchedWorks = await worksService.fetchWorksByIds(Array.from(workIdSet));
        for (const work of fetchedWorks) {
          const candidates: Array<string | undefined> = [work?.id as string | undefined, (work as any)?.workId];
          candidates.forEach((candidate) => {
            if (!candidate || typeof candidate !== 'string') return;
            const trimmed = candidate.trim();
            if (trimmed) {
              workMetadata.set(trimmed, work as Record<string, any>);
            }
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to load batch work metadata', error);
        }
      }
    }

    const recordings = buildRecordingList(workMetadata);
    try {
      await thunkAPI.dispatch(persistLibraryCache({ recordings })).unwrap();
    } catch {
      // ignore cache write failures
    }
    return recordings;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[yourLibrary] Failed to fetch favorite recordings', error);
    }
    return [];
  }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { recordingsStatus, lastFetchedAt, myRecordings } = state.yourLibrary;
      const lastFetched = lastFetchedAt?.recordings ?? null;
      if (process.env.NODE_ENV === 'development') {
        console.info('[RECFETCH] condition check', {
          recordingsStatus,
          lastFetched,
          recordingsCount: myRecordings?.length ?? 0,
        });
      }
      return recordingsStatus !== 'loading';
    },
  }
);

export const persistLibraryCache = createAsyncThunk<
  void,
  Partial<LibraryLists> | undefined,
  { state: RootState; dispatch: AppDispatch }
>('yourLibrary/persistLibraryCache', async (overrides, { getState }) => {
  const state = getState();
  const userId = state.auth.user?.id?.trim();
  if (!userId) return;

  const lists: LibraryLists = {
    albums: overrides?.albums ?? state.yourLibrary.myAlbums,
    artists: overrides?.artists ?? state.yourLibrary.myArtists,
    playlists: overrides?.playlists ?? state.yourLibrary.myPlaylists,
    composers: overrides?.composers ?? state.yourLibrary.myComposers,
    works: overrides?.works ?? state.yourLibrary.myWorks,
    recordings: overrides?.recordings ?? state.yourLibrary.myRecordings,
  };

  try {
    await Promise.all([
      libraryCache.persistBoot(userId, lists),
      libraryCache.persistFull(userId, lists),
      libraryCache.persistFullWorksRecordings(userId, lists),
    ]);
  } catch {
    // Swallow cache errors; runtime should continue.
  }
});

export const fetchLibraryCoreFromDb = createAsyncThunk<
  { artists: Artist[]; playlists: Playlist[]; albums: Album[]; composers: LibraryComposer[] },
  void,
  { state: RootState; dispatch: AppDispatch }
>('yourLibrary/fetchLibraryCoreFromDb', async (_, { getState, dispatch }) => {
  const state = getState();
  const userId = state.auth.user?.id?.trim();
  if (!userId) {
    throw new Error('Missing user id');
  }

  const [artists, playlists, albums, composers] = await Promise.all([
    favoriteArtistService.getFavoriteArtists(),
    favoritePlaylistsService.listFavoritePlaylists(),
    libraryAlbumsService.fetchCachedAlbums(userId),
    libraryService.fetchComposers(),
  ]);

  if (process.env.NODE_ENV === 'development') {
    // Log a small sample to verify payload shape from DB cache
    const sampleAlbums = albums.slice(0, 3).map((album) => ({
      id: album.id,
      name: album.name,
      artists: album.artists?.map((a) => a.name),
      image: album.images?.[0]?.url,
      releaseDate: album.release_date,
      totalTracks: album.total_tracks,
    }));
    console.info('[LibraryCache] DB core sample', {
      albums: sampleAlbums,
      artists: artists.slice(0, 3).map((a) => ({ id: a.spotifyArtistId, name: a.name })),
      playlists: playlists.slice(0, 3).map((p) => ({ id: p.spotifyPlaylistId, name: p.name })),
      composers: composers.slice(0, 3).map((c) => ({ id: c.id, name: c.name })),
    });
  }

  const normalizedArtists: Artist[] = artists.map((artist) => ({
    id: artist.spotifyArtistId,
    type: 'artist',
    name: artist.name ?? '',
    uri: artist.uri ?? `spotify:artist:${artist.spotifyArtistId}`,
    images: artist.imageUrl ? [{ url: artist.imageUrl, height: 0, width: 0 }] : [],
    genres: Array.isArray(artist.genres)
      ? artist.genres.filter((g: unknown): g is string => typeof g === 'string')
      : [],
    external_urls: { spotify: '' },
    followers: { href: '', total: 0 },
    href: '',
    popularity: 0,
  }));

  const normalizedPlaylists: Playlist[] = playlists.map((playlist) => ({
    id: playlist.spotifyPlaylistId,
    type: 'playlist',
    name: playlist.name ?? '',
    uri: playlist.uri ?? `spotify:playlist:${playlist.spotifyPlaylistId}`,
    images: playlist.imageUrl ? [{ url: playlist.imageUrl, height: 0, width: 0 }] : [],
    owner: {
      id: playlist.ownerId ?? userId ?? '',
      display_name: playlist.ownerDisplayName ?? '',
      external_urls: { spotify: '' },
      href: '',
      type: 'user',
      uri: '',
      followers: { href: '', total: 0 },
    } as any,
    description: playlist.description ?? null,
    followers: { href: '', total: 0 },
    public: null,
    collaborative: false,
    href: '',
    external_urls: { spotify: '' },
    snapshot_id: '',
    tracks: { href: '', total: 0 },
  }));

  const normalizedComposers: LibraryComposer[] = composers.map((composer) => ({
    id: composer.id ?? composer.spotifyArtistId ?? '',
    type: 'composer',
    name: composer.name ?? '',
    imageUrl: composer.imageUrl,
    uri: composer.uri ?? composer.id ?? '',
    spotifyArtistId: composer.spotifyArtistId,
    era: composer.era ?? undefined,
    birthYear:
      typeof composer.birthYear === 'number' ? String(composer.birthYear) : composer.birthYear ?? undefined,
    deathYear:
      typeof composer.deathYear === 'number' ? String(composer.deathYear) : composer.deathYear ?? undefined,
  }));

  dispatch(yourLibraryActions.upsertMyArtists(normalizedArtists));
  dispatch(yourLibraryActions.upsertMyPlaylists(normalizedPlaylists));
  dispatch(yourLibraryActions.upsertMyAlbums(albums));
  dispatch(yourLibraryActions.setMyComposers(normalizedComposers));

  await dispatch(
    persistLibraryCache({
      artists: normalizedArtists,
      playlists: normalizedPlaylists,
      albums,
      composers: normalizedComposers,
    })
  ).unwrap();

  return { artists: normalizedArtists, playlists: normalizedPlaylists, albums, composers: normalizedComposers };
});

export const importLibraryFromSpotify = createAsyncThunk<
  { artists: Artist[]; playlists: Playlist[]; albums: Album[] },
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>('yourLibrary/importLibraryFromSpotify', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const userId = state.auth.user?.id?.trim();
  if (!userId) {
    dispatch(uiActions.openLoginTooltip());
    libLog('import abort: missing user');
    void logAppEvent({
      eventName: 'import_library_missing_user',
      level: 'warn',
      message: 'Import attempted without a user id',
    });
    return rejectWithValue('Please log in to import your library.');
  }

  const tokenCandidates: Array<string | undefined> = [
    state.auth.token,
    getFromLocalStorageWithExpiry('access_token') as string | undefined,
    getFromLocalStorageWithExpiry('public_access_token') as string | undefined,
  ];
  const accessTokenCandidate = tokenCandidates.find(
    (token) => typeof token === 'string' && token.trim().length > 0
  );
  const accessToken = accessTokenCandidate?.trim();
  if (!accessToken) {
    dispatch(uiActions.openLoginTooltip());
    libLog('import abort: missing access token');
    void logAppEvent({
      eventName: 'import_library_missing_access_token',
      level: 'warn',
      userId,
      message: 'Import attempted without a Spotify access token',
    });
    return rejectWithValue('Spotify access token missing. Please log in and try again.');
  }

  const devPageLimit = isDevUser(userId) ? 1 : Infinity;

  const mapSavedTrackToImportPayload = (item: any): ImportTrackPayload | null => {
    const track = item?.track;
    const trackId = normalizeSpotifyTrackId(track?.id);
    if (!trackId) {
      return null;
    }

    const album = track?.album && typeof track.album === 'object' ? track.album : null;
    const albumImages =
      album && Array.isArray((album as any).images)
        ? (album as any).images
            .filter((image: any) => typeof image?.url === 'string' && image.url.trim().length > 0)
            .slice(0, 1)
            .map((image: any) => ({
              url: String(image.url).trim(),
              width: typeof image.width === 'number' ? image.width : undefined,
              height: typeof image.height === 'number' ? image.height : undefined,
            }))
        : undefined;

    const artists =
      Array.isArray(track?.artists) && track.artists.length > 0
        ? track.artists
            .map((artist: any) => ({
              id: typeof artist?.id === 'string' ? artist.id : undefined,
              uri: typeof artist?.uri === 'string' ? artist.uri : undefined,
              name: typeof artist?.name === 'string' ? artist.name : undefined,
            }))
            .filter(
              (artist: { id?: string; uri?: string; name?: string }) =>
                typeof artist.id === 'string' || typeof artist.name === 'string'
            )
        : undefined;

    const payload: Record<string, unknown> = {
      added_at: typeof item?.added_at === 'string' ? item.added_at : undefined,
      is_local: typeof item?.is_local === 'boolean' ? item.is_local : undefined,
      track: {
        id: trackId,
        uri: typeof track?.uri === 'string' ? track.uri : undefined,
        name: typeof track?.name === 'string' ? track.name : undefined,
        duration_ms: typeof track?.duration_ms === 'number' ? track.duration_ms : undefined,
        explicit: typeof track?.explicit === 'boolean' ? track.explicit : undefined,
        popularity: typeof track?.popularity === 'number' ? track.popularity : undefined,
        album:
          album && typeof album === 'object'
            ? {
                id: typeof album?.id === 'string' ? album.id : undefined,
                uri: typeof album?.uri === 'string' ? album.uri : undefined,
                name: typeof album?.name === 'string' ? album.name : undefined,
                images: albumImages,
                release_date: typeof album?.release_date === 'string' ? album.release_date : undefined,
              }
            : undefined,
        artists: artists && artists.length ? artists : undefined,
        external_urls:
          track?.external_urls && typeof track.external_urls === 'object' && typeof track.external_urls.spotify === 'string'
            ? { spotify: track.external_urls.spotify }
            : undefined,
        preview_url:
          'preview_url' in (track ?? {})
            ? typeof track?.preview_url === 'string'
              ? track.preview_url
              : null
            : undefined,
      },
    };

    return {
      spotifyTrackId: trackId,
      addedAt: typeof item?.added_at === 'string' ? item.added_at : undefined,
      payload,
    };
  };

  try {
    libLog('import start');
    const allArtists: Artist[] = [];
    let artistAfter: string | undefined;
    let artistPagesFetched = 0;
    // Fetch followed artists (cursor-based)
    // eslint-disable-next-line no-constant-condition
    while (artistPagesFetched < devPageLimit) {
      const { data } = await userService.fetchFollowedArtists({ limit: 50, after: artistAfter });
      artistPagesFetched += 1;
      const pageItems = data.artists.items ?? [];
      if (pageItems.length) {
        allArtists.push(...pageItems);
      }
      const nextAfter = data.artists.cursors?.after ?? undefined;
      if (
        !data.artists.next ||
        !nextAfter ||
        nextAfter === artistAfter ||
        pageItems.length === 0
      ) {
        break;
      }
      artistAfter = nextAfter;
    }

    const allPlaylists: Playlist[] = [];
    let playlistOffset = 0;
    const playlistLimit = 50;
    let playlistPagesFetched = 0;
    // eslint-disable-next-line no-constant-condition
    while (playlistPagesFetched < devPageLimit) {
      const { data } = await playlistService.getMyPlaylists({ limit: playlistLimit, offset: playlistOffset });
      playlistPagesFetched += 1;
      const items = data.items ?? [];
      if (items.length) {
        allPlaylists.push(...items);
      }
      if (!data.next || items.length === 0) break;
      playlistOffset += playlistLimit;
    }

    const allAlbumEntries: Array<{ album: Album; addedAt?: string }> = [];
    let albumOffset = 0;
    const albumLimit = 50;
    let albumPagesFetched = 0;
    // eslint-disable-next-line no-constant-condition
    while (albumPagesFetched < devPageLimit) {
      const { data } = await albumsService.fetchSavedAlbums({ limit: albumLimit, offset: albumOffset });
      albumPagesFetched += 1;
      const items = data.items ?? [];
      if (items.length) {
        for (const entry of items) {
          const album = entry?.album;
          if (album?.id) {
            allAlbumEntries.push({
              album,
              addedAt: typeof (entry as any)?.added_at === 'string' ? (entry as any).added_at : undefined,
            });
          }
        }
      }
      if (!data.next || items.length === 0) break;
      albumOffset += albumLimit;
    }
    const allAlbums = allAlbumEntries.map((entry) => entry.album);

    const tracksPayload: ImportTrackPayload[] = [];
    let trackOffset = 0;
    const trackLimit = 50;
    let trackPagesFetched = 0;
    // eslint-disable-next-line no-constant-condition
    while (trackPagesFetched < devPageLimit) {
      const { data } = await userService.getSavedTracks({ limit: trackLimit, offset: trackOffset });
      trackPagesFetched += 1;
      const items = data.items ?? [];
      if (items.length) {
        for (const item of items) {
          const payload = mapSavedTrackToImportPayload(item);
          if (payload) {
            tracksPayload.push(payload);
          }
        }
      }
      if (!data.next || items.length === 0) break;
      trackOffset += trackLimit;
    }

    if (devPageLimit !== Infinity) {
      libLog('dev page cap applied', {
        devPageLimit,
        artistPagesFetched,
        playlistPagesFetched,
        albumPagesFetched,
        trackPagesFetched,
      });
    }

    // Normalize payloads for backend persistence
    const artistsPayload: ImportArtistPayload[] = allArtists.map((artist) => ({
      spotifyArtistId: artist.id,
      name: artist.name,
      imageUrl: firstImageUrl(artist.images),
      uri: artist.uri,
      genres: Array.isArray(artist.genres)
        ? artist.genres.filter((genre): genre is string => typeof genre === 'string' && genre.trim().length > 0)
        : undefined,
      savedAt: new Date().toISOString(),
    }));

    const playlistsPayload: ImportPlaylistPayload[] = allPlaylists.map((playlist) => ({
      spotifyPlaylistId: playlist.id,
      name: playlist.name,
      uri: playlist.uri,
      ownerDisplayName: playlist.owner?.display_name ?? null,
      description: typeof playlist.description === 'string' ? playlist.description : null,
      imageUrl: firstImageUrl(playlist.images),
      savedAt: new Date().toISOString(),
      lastPlayedAt: null,
    }));

    const albumsPayload: ImportAlbumPayload[] = allAlbumEntries.map((entry) => ({
      spotifyAlbumId: entry.album.id,
      name: entry.album.name,
      primaryArtistNames: Array.isArray(entry.album.artists)
        ? entry.album.artists
            .map((artist) => (typeof artist?.name === 'string' ? artist.name.trim() : ''))
            .filter((artist) => artist.length > 0)
        : [],
      imageUrl: firstImageUrl(entry.album.images),
      releaseDate: typeof entry.album.release_date === 'string' ? entry.album.release_date : undefined,
      totalTracks: typeof entry.album.total_tracks === 'number' ? entry.album.total_tracks : undefined,
      uri: entry.album.uri,
      savedAt: new Date().toISOString(),
      addedAt: entry.addedAt,
    }));

    try {
      await loadCatalogComposerIds();
    } catch {
      // ignore warming errors
    }
    const knownComposerIds = getKnownComposerIds();
    const composersPayload: ImportComposerPayload[] = allArtists
      .filter((artist) => artist?.id && knownComposerIds.has(artist.id))
      .map((artist) => {
        const composerId = getCatalogIdForSpotifyId(artist.id) ?? artist.id;
        return {
          composerId,
          spotifyArtistId: artist.id,
          name: artist.name,
          imageUrl: firstImageUrl(artist.images),
          uri: artist.uri,
          era: null,
          birthYear: null,
          deathYear: null,
          savedAt: new Date().toISOString(),
        };
      });

    await libraryImportService.persistSpotifyLibrary({
      artists: artistsPayload,
      playlists: playlistsPayload,
      albums: albumsPayload,
      composers: composersPayload,
      tracks: tracksPayload,
    });
    libLog('import backend persisted', {
      artists: artistsPayload.length,
      playlists: playlistsPayload.length,
      albums: albumsPayload.length,
      composers: composersPayload.length,
      tracks: tracksPayload.length,
    });

    try {
      const cachedArtists: Artist[] = artistsPayload.map((artist) => ({
        id: artist.spotifyArtistId,
        type: 'artist',
        name: artist.name ?? '',
        uri: artist.uri ?? `spotify:artist:${artist.spotifyArtistId}`,
        images: artist.imageUrl ? [{ url: artist.imageUrl, height: 0, width: 0 }] : [],
        genres: artist.genres ?? [],
        external_urls: { spotify: '' },
        followers: { href: '', total: 0 },
        href: '',
        popularity: 0,
      }));

      const cachedPlaylists: Playlist[] = playlistsPayload.map((playlist) => ({
        id: playlist.spotifyPlaylistId,
        type: 'playlist',
        name: playlist.name ?? '',
        uri: playlist.uri ?? `spotify:playlist:${playlist.spotifyPlaylistId}`,
        images: playlist.imageUrl ? [{ url: playlist.imageUrl, height: 0, width: 0 }] : [],
        owner: {
          id: '',
          display_name: playlist.ownerDisplayName ?? '',
          external_urls: { spotify: '' },
          href: '',
          type: 'user',
          uri: '',
          followers: { href: '', total: 0 },
        } as any,
        description: playlist.description ?? null,
        followers: { href: '', total: 0 },
        public: null,
        collaborative: false,
        href: '',
        external_urls: { spotify: '' },
        snapshot_id: '',
        tracks: { href: '', total: 0 },
      }));

      const cachedAlbums: Album[] = albumsPayload.map((album) => ({
        id: album.spotifyAlbumId,
        type: 'album',
        album_type: 'album',
        name: album.name ?? '',
        uri: album.uri ?? `spotify:album:${album.spotifyAlbumId}`,
        images: album.imageUrl ? [{ url: album.imageUrl, height: 0, width: 0 }] : [],
        artists: (album.primaryArtistNames ?? []).map((name, index) => ({
          id: `${album.spotifyAlbumId}-artist-${index}`,
          name,
          type: 'artist',
          uri: '',
          href: '',
          external_urls: { spotify: '' },
        })),
        available_markets: [],
        external_urls: { spotify: '' },
        href: '',
        release_date: album.releaseDate ?? '',
        release_date_precision: 'day',
        total_tracks: album.totalTracks ?? 0,
      }));

      const cachedComposers: LibraryComposer[] = composersPayload.map((composer) => ({
        id: composer.composerId,
        type: 'composer',
        name: composer.name ?? '',
        imageUrl: composer.imageUrl,
        uri: composer.uri ?? composer.composerId,
        spotifyArtistId: composer.spotifyArtistId,
        era: composer.era ?? undefined,
        birthYear:
          typeof composer.birthYear === 'number'
            ? String(composer.birthYear)
            : composer.birthYear ?? undefined,
        deathYear:
          typeof composer.deathYear === 'number'
            ? String(composer.deathYear)
            : composer.deathYear ?? undefined,
      }));

      const cacheUserId = userId ?? '';
      await dispatch(
        persistLibraryCache({
          artists: cachedArtists,
          playlists: cachedPlaylists,
          albums: cachedAlbums,
          composers: cachedComposers,
        })
      ).unwrap();
      libLog('import caches persisted', {
        artists: cachedArtists.length,
        playlists: cachedPlaylists.length,
        albums: cachedAlbums.length,
        composers: cachedComposers.length,
        userId: cacheUserId,
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Failed to persist library caches', error);
      }
      libLog('import cache persist failed', { error });
    }

    libLog('import complete', {
      artists: allArtists.length,
      playlists: allPlaylists.length,
      albums: allAlbums.length,
      composers: composersPayload.length,
    });
    return { artists: allArtists, playlists: allPlaylists, albums: allAlbums };
  } catch (error) {
    const fallbackMessage = 'Failed to import library from Spotify.';
    const errorMessage = extractErrorMessage(error, fallbackMessage);
    if (process.env.NODE_ENV === 'development') {
      console.error('[yourLibrary] importLibraryFromSpotify failed', error);
    }
    void logAppEvent({
      eventName: 'import_library_failed',
      level: 'error',
      userId,
      message: errorMessage,
      stack: error instanceof Error ? error.stack ?? null : null,
      context: {
        spotifyUserId: userId,
        hasAccessToken: Boolean(accessToken),
      },
    });
    notifyError(errorMessage);
    return rejectWithValue(errorMessage);
  }
},
{
  condition: (_, { getState }) => {
    const state = getState() as RootState;
    return state.yourLibrary.importStatus !== 'pending';
  }
});

export const loadLibrarySyncSettings = createAsyncThunk<
  LibrarySyncSettingsValue,
  void,
  { rejectValue: string }
>('yourLibrary/loadLibrarySyncSettings', async (_, { rejectWithValue }) => {
  try {
    return await librarySyncSettingsService.fetchSettings();
  } catch (error) {
    const fallbackMessage = 'Failed to load library sync settings.';
    const errorMessage = extractErrorMessage(error, fallbackMessage);
    if (process.env.NODE_ENV === 'development') {
      console.error('[yourLibrary] loadLibrarySyncSettings failed', error);
    }
    notifyError(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const updateLibrarySyncAutomation = createAsyncThunk<
  LibrarySyncSettingsValue,
  { ruleId: LibrarySyncAutomationId; enabled: boolean },
  { state: RootState; rejectValue: string }
>('yourLibrary/updateLibrarySyncAutomation', async ({ ruleId, enabled }, { getState, rejectWithValue }) => {
  const currentSettings = getState().yourLibrary.librarySyncSettings.value;
  if (!currentSettings) {
    const errorMessage = 'Library sync settings are not ready yet.';
    notifyError(errorMessage);
    return rejectWithValue(errorMessage);
  }

  const updatePayload: LibrarySyncSettingsUpdate = {
    version: currentSettings.version,
    automations: {
      [ruleId]: {
        enabled,
      },
    },
  };

  try {
    return await librarySyncSettingsService.updateSettings(updatePayload);
  } catch (error) {
    const fallbackMessage = 'Failed to update library sync settings.';
    const errorMessage = extractErrorMessage(error, fallbackMessage);
    if (process.env.NODE_ENV === 'development') {
      console.error('[yourLibrary] updateLibrarySyncAutomation failed', error);
    }
    notifyError(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const saveRecordingWithCascade = createAsyncThunk<
  { favorite: FavoriteRecording; cascadedWorkId?: string },
  {
    recordingId: string;
    workId?: string;
    notes?: string;
    labels?: string[];
    savedAt?: string | number | Date;
    id?: string;
    cascades?: { tracks?: boolean };
    trackIds?: string[] | null;
  },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/saveRecordingWithCascade',
  async (
    { recordingId, workId: upstreamWorkId, notes, labels, savedAt, id, cascades, trackIds },
    { dispatch, getState, rejectWithValue }
  ) => {
    if (!recordingId) {
      const errorMessage = 'Recording ID is required.';
      return rejectWithValue(errorMessage);
    }

    const detailCache: RecordingDetailCache = new Map();

    let resolvedWorkId: string | undefined = stringOrUndefined(upstreamWorkId);
    if (!resolvedWorkId) {
      try {
        resolvedWorkId = await resolveCanonicalRecordingWorkId(recordingId, detailCache);
        if (!resolvedWorkId && process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] No canonical work mapping found for recording', recordingId);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to load canonical work mapping for recording', recordingId, error);
        }
      }
    }

    const normalizedTrackIds =
      Array.isArray(trackIds) && trackIds.length
        ? Array.from(
            new Set(
              trackIds
                .map((candidate) => (typeof candidate === 'string' ? candidate.trim() : ''))
                .filter((candidate) => candidate.length > 0)
            )
          )
        : null;

    let trackIdsForRecording: string[] = normalizedTrackIds ?? [];
    if (!trackIdsForRecording.length) {
      try {
        trackIdsForRecording = await resolveRecordingTrackIds(recordingId, detailCache);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to resolve recording tracks', recordingId, error);
        }
      }
    }

    const defaults = selectRecordingCascadeDefaults(getState());
    const shouldCascadeTracks = cascades?.tracks ?? defaults.save.tracks;

    const action = await dispatch(
      saveRecordingsWithCascade({
        recordingIds: [recordingId],
        options: {
          notes,
          labels,
          savedAt,
          id,
          workIdsByRecording: resolvedWorkId
            ? {
                [recordingId]: resolvedWorkId,
              }
            : undefined,
          resolveMissingWorkIds: false,
          trackIdsByRecording: trackIdsForRecording.length
            ? { [recordingId]: trackIdsForRecording }
            : undefined,
          cascades: {
            tracks: shouldCascadeTracks,
          },
        },
      })
    );

    if (saveRecordingsWithCascade.rejected.match(action)) {
      const payload = action.payload;
      const errorMessage =
        typeof payload === 'string' && payload.trim().length > 0
          ? payload
          : 'Failed to save recording.';
      return rejectWithValue(errorMessage);
    }

    const payload = action.payload as SaveRecordingsWithCascadeResult;
    let favorite = payload.favorites[0];

    if (!favorite) {
      const fallbackMessage = 'Failed to save recording.';
      return rejectWithValue(fallbackMessage);
    }

    if (resolvedWorkId && !favorite.workId) {
      favorite = {
        ...favorite,
        workId: resolvedWorkId,
      };
    }

    const cascadedWorkId = resolvedWorkId
      ? resolvedWorkId
      : payload.cascadedWorkIds.find((workId) => typeof workId === 'string' && workId.length > 0);

    return { favorite, cascadedWorkId };
  }
);

export const removeRecordingWithCascade = createAsyncThunk<
  { recordingId: string },
  {
    recordingId: string;
    cascades?: { tracks?: boolean };
    trackIds?: string[] | null;
  },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/removeRecordingWithCascade',
  async ({ recordingId, cascades, trackIds }, { dispatch, getState, rejectWithValue }) => {
    if (!recordingId) {
      return rejectWithValue('Recording ID is required.');
    }

    const normalizedTrackIds =
      Array.isArray(trackIds) && trackIds.length
        ? Array.from(
            new Set(
              trackIds
                .map((id) => (typeof id === 'string' ? id.trim() : ''))
                .filter((id) => id.length > 0)
            )
          )
        : null;

    let trackIdsForRecording: string[] = normalizedTrackIds ?? [];
    if (!trackIdsForRecording.length) {
      try {
        trackIdsForRecording = await resolveRecordingTrackIds(recordingId);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to resolve track IDs for recording removal', recordingId, error);
        }
      }
    }

    const defaults = selectRecordingCascadeDefaults(getState());
    const shouldCascadeTracks = cascades?.tracks ?? defaults.remove.tracks;

    try {
      await dispatch(
        removeRecordingsWithCascade({
          recordingIds: [recordingId],
          options: {
            trackIdsByRecording: trackIdsForRecording.length
              ? { [recordingId]: trackIdsForRecording }
              : undefined,
            cascades: {
              tracks: shouldCascadeTracks,
            },
          },
        })
      ).unwrap();
      return { recordingId };
    } catch (error) {
      const errorMessage =
        typeof error === 'string' && error.trim().length > 0 ? error : 'Failed to remove recording.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeWorkWithCascade = createAsyncThunk<
  { workId: string; cascadedRecordingCount: number; removedWork: boolean },
  {
    workId: string;
    workName?: string | null;
    cascades?: { recordings?: boolean };
    removeWork?: boolean;
  },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/removeWorkWithCascade',
  async ({ workId, workName, cascades, removeWork }, { dispatch, getState, rejectWithValue }) => {
    const trimmedWorkId = typeof workId === 'string' ? workId.trim() : '';
    if (!trimmedWorkId) {
      return rejectWithValue('Work ID is required.');
    }

    const workLabel = workName ? `"${workName}"` : 'the work';
    const shouldRemoveWork = removeWork !== false;

    if (shouldRemoveWork) {
      try {
        await favoriteWorksService.removeFavoriteWork(trimmedWorkId);
        message.success(`Removed ${workLabel} from Your Library`);
      } catch (error) {
        const fallbackMessage = 'Failed to remove work.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        if (process.env.NODE_ENV === 'development') {
          console.error('[yourLibrary] Failed to remove work favorite', trimmedWorkId, error);
        }
        notifyError(errorMessage);
        return rejectWithValue(errorMessage);
      }
    }

    const state = getState();
    const defaults = selectWorkCascadeDefaults(state);
    const shouldCascadeRecordings = cascades?.recordings ?? defaults.remove.recordings;

    let cascadedRecordingCount = 0;

    if (shouldCascadeRecordings) {
      const savedRecordings = state.yourLibrary.myRecordings.filter(
        (entry) => entry?.workId === trimmedWorkId
      );
      const recordingIds = Array.from(
        new Set(
          savedRecordings
            .map((entry) => (typeof entry?.id === 'string' ? entry.id.trim() : ''))
            .filter((id): id is string => id.length > 0)
        )
      );

      if (recordingIds.length > 0) {
        try {
          const result = await dispatch(
            removeRecordingsWithCascade({
              recordingIds,
            })
          ).unwrap();
          cascadedRecordingCount = result.removed;
          if (cascadedRecordingCount > 0) {
            const noun = cascadedRecordingCount === 1 ? 'recording' : 'recordings';
            message.success(`Also removed ${cascadedRecordingCount} ${noun} linked to ${workLabel}`);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[yourLibrary] Failed to cascade work recording removals',
              trimmedWorkId,
              recordingIds,
              error
            );
          }
          message.warning(
            shouldRemoveWork
              ? 'Removed the work, but could not remove its recordings. Please try again in a bit.'
              : 'Could not remove recordings for this work. Please try again in a bit.'
          );
        }
      }
    }

    if (shouldRemoveWork) {
      void dispatch(fetchMyWorks());
    }
    void dispatch(fetchMyRecordings());

    return { workId: trimmedWorkId, cascadedRecordingCount, removedWork: shouldRemoveWork };
  }
);

type AlbumCascadeOverrides = {
  tracks?: boolean;
  recordings?: boolean;
};

type AlbumCascadePayload = {
  albumId: string;
  albumName?: string;
  cascades?: AlbumCascadeOverrides;
  trackIds?: string[] | null;
};

type PlaylistCascadeOverrides = {
  playlist?: boolean;
  recordings?: boolean;
  tracks?: boolean;
};

type PlaylistCascadePayload = {
  playlistId: string;
  playlistName?: string;
  cascades?: PlaylistCascadeOverrides;
};

export const saveAlbumWithCascade = createAsyncThunk<
  {
    cascadedTrackCount: number;
    cascadedRecordingCount: number;
    recordingSummary?: AlbumRecordingSaveSummary;
  },
  AlbumCascadePayload,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/saveAlbumWithCascade',
  async ({ albumId, albumName, cascades, trackIds }, { dispatch, getState, rejectWithValue }) => {
    const albumLabel = albumName ? `"${albumName}"` : 'the album';

    try {
      await albumsService.saveAlbums([albumId]);
      message.success(`Saved ${albumLabel} to Your Library`);
      void dispatch(fetchMyAlbums());
    } catch (error) {
      const fallbackMessage = 'Failed to save album.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] saveAlbumWithCascade failed to save album', error);
      }
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const state = getState();
    const currentAlbum =
      state.album?.album && state.album.album?.id === albumId ? state.album.album : undefined;
    const metadata =
      currentAlbum && typeof currentAlbum === 'object'
        ? {
            title: currentAlbum.name,
            primaryArtistNames: Array.isArray(currentAlbum.artists)
              ? currentAlbum.artists
                  .map((artist) => artist?.name)
                  .filter((name): name is string => typeof name === 'string' && name.length > 0)
              : undefined,
            imageUrl:
              Array.isArray(currentAlbum.images) && currentAlbum.images.length > 0
                ? currentAlbum.images[0]?.url
                : undefined,
            totalTracks:
              typeof currentAlbum.total_tracks === 'number'
                ? currentAlbum.total_tracks
                : undefined,
            releaseDate:
              typeof currentAlbum.release_date === 'string' && currentAlbum.release_date.length > 0
                ? currentAlbum.release_date
                : undefined,
          }
        : undefined;

    try {
      await favoriteAlbumsService.saveFavoriteAlbum(albumId, {
        metadata: metadata ?? undefined,
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Failed to persist favorite album', albumId, error);
      }
      message.warning(
        'Saved the album, but we could not update your favorites. It will sync shortly.'
      );
    }

    const automations = state.yourLibrary.librarySyncSettings.value.automations;
    const shouldCascadeTracks =
      cascades?.tracks ??
      (automations?.albumSaveCascade?.enabled ?? false);
    const shouldCascadeRecordings =
      cascades?.recordings ??
      (automations?.albumSaveRecordings?.enabled ?? false);

    const normalizedTrackIds =
      Array.isArray(trackIds) && trackIds.length
        ? Array.from(
            new Set(
              trackIds
                .map((id) => (typeof id === 'string' ? id.trim() : ''))
                .filter((id) => id.length > 0)
            )
          )
        : null;

    let sharedTrackIds: string[] | null = normalizedTrackIds;
    let cascadedTrackCount = 0;
    let recordingSummary: AlbumRecordingSaveSummary | undefined;

    if (shouldCascadeTracks) {
      try {
        sharedTrackIds = sharedTrackIds ?? (await collectAlbumTrackIds(albumId));
        if (sharedTrackIds.length > 0) {
          cascadedTrackCount = await cascadeSaveTracks(sharedTrackIds);
          const currentAlbum = getState().album.album;
          if (currentAlbum?.id === albumId) {
            dispatch(
              albumActions.setTracksSavedState({ trackIds: sharedTrackIds, saved: true })
            );
          }
          if (cascadedTrackCount > 0) {
            const noun = cascadedTrackCount === 1 ? 'track' : 'tracks';
            message.success(`Also saved ${cascadedTrackCount} ${noun} from ${albumLabel}`);
          }
        }
      } catch (error) {
        console.error('[yourLibrary] Failed to cascade album tracks', error);
        message.warning(
          'Saved the album, but we could not save its tracks. Please try again in a bit.'
        );
      }
    }

    if (shouldCascadeRecordings) {
      try {
        sharedTrackIds = sharedTrackIds ?? (await collectAlbumTrackIds(albumId));
        recordingSummary = await cascadeSaveRecordings(dispatch, albumId, sharedTrackIds ?? undefined, {
          skipTrackAutomation: shouldCascadeTracks,
        });
        if (recordingSummary.totalRecordings > 0 && recordingSummary.saved > 0) {
          const noun = recordingSummary.saved === 1 ? 'recording' : 'recordings';
          message.success(`Also saved ${recordingSummary.saved} ${noun} from ${albumLabel}`);
        } else if (
          recordingSummary.totalTracks > 0 &&
          recordingSummary.totalRecordings === 0 &&
          process.env.NODE_ENV === 'development'
        ) {
          console.info('[yourLibrary] No recordings matched for album cascade', {
            albumId,
            albumName,
          });
        }
      } catch (error) {
        console.error('[yourLibrary] Failed to cascade album recordings', error);
        message.warning(
          'Saved the album, but we could not save its recordings. Please try again in a bit.'
        );
      }
      void dispatch(fetchMyRecordings());
    }

    return {
      cascadedTrackCount,
      cascadedRecordingCount: recordingSummary?.saved ?? 0,
      recordingSummary,
    };
  }
);

export const removeAlbumWithCascade = createAsyncThunk<
  {
    cascadedTrackCount: number;
    cascadedRecordingCount: number;
    recordingSummary?: AlbumRecordingRemoveSummary;
  },
  AlbumCascadePayload,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/removeAlbumWithCascade',
  async ({ albumId, albumName, cascades, trackIds }, { dispatch, getState, rejectWithValue }) => {
    const albumLabel = albumName ? `"${albumName}"` : 'the album';

    try {
      await albumsService.deleteAlbums([albumId]);
      message.success(`Removed ${albumLabel} from Your Library`);
      void dispatch(fetchMyAlbums());
    } catch (error) {
      const fallbackMessage = 'Failed to remove album.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] removeAlbumWithCascade failed to remove album', error);
      }
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const state = getState();

    try {
      await favoriteAlbumsService.removeFavoriteAlbum(albumId);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[yourLibrary] Failed to remove favorite album', albumId, error);
      }
      message.warning(
        'Removed the album from Spotify, but we could not update your favorites. It will sync shortly.'
      );
    }

    const automations = state.yourLibrary.librarySyncSettings.value.automations;
    const shouldCascadeTracks =
      cascades?.tracks ??
      (automations?.albumUnsaveCascade?.enabled ?? false);
    const shouldCascadeRecordings =
      cascades?.recordings ??
      (automations?.albumUnsaveRecordings?.enabled ?? false);

    const normalizedTrackIds =
      Array.isArray(trackIds) && trackIds.length
        ? Array.from(
            new Set(
              trackIds
                .map((id) => (typeof id === 'string' ? id.trim() : ''))
                .filter((id) => id.length > 0)
            )
          )
        : null;

    let sharedTrackIds: string[] | null = normalizedTrackIds;
    let cascadedTrackCount = 0;
    let recordingSummary: AlbumRecordingRemoveSummary | undefined;

    if (shouldCascadeTracks) {
      try {
        sharedTrackIds = sharedTrackIds ?? (await collectAlbumTrackIds(albumId));
        if (sharedTrackIds.length > 0) {
          cascadedTrackCount = await cascadeDeleteTracks(sharedTrackIds);
          const currentAlbum = getState().album.album;
          if (currentAlbum?.id === albumId) {
            dispatch(
              albumActions.setTracksSavedState({ trackIds: sharedTrackIds, saved: false })
            );
          }
          if (cascadedTrackCount > 0) {
            const noun = cascadedTrackCount === 1 ? 'track' : 'tracks';
            message.success(`Also removed ${cascadedTrackCount} ${noun} from ${albumLabel}`);
          }
        }
      } catch (error) {
        console.error('[yourLibrary] Failed to cascade album track removal', error);
        message.warning(
          'Removed the album, but we could not remove its tracks. Please try again in a bit.'
        );
      }
    }

    if (shouldCascadeRecordings) {
      try {
        sharedTrackIds = sharedTrackIds ?? (await collectAlbumTrackIds(albumId));
        recordingSummary = await cascadeDeleteRecordings(
          dispatch,
          albumId,
          sharedTrackIds ?? undefined,
          { skipTrackAutomation: shouldCascadeTracks }
        );
        if (recordingSummary.totalRecordings > 0 && recordingSummary.removed > 0) {
          const noun = recordingSummary.removed === 1 ? 'recording' : 'recordings';
          message.success(`Also removed ${recordingSummary.removed} ${noun} from ${albumLabel}`);
        }
      } catch (error) {
        console.error('[yourLibrary] Failed to cascade album recording removal', error);
        message.warning(
          'Removed the album, but we could not remove its recordings. Please try again in a bit.'
        );
      }
      void dispatch(fetchMyRecordings());
    }

    return {
      cascadedTrackCount,
      cascadedRecordingCount: recordingSummary?.removed ?? 0,
      recordingSummary,
    };
  }
);

export const savePlaylistWithCascade = createAsyncThunk<
  { cascadedTrackCount: number; cascadedRecordingCount: number; totalItems: number },
  PlaylistCascadePayload,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/savePlaylistWithCascade',
  async ({ playlistId, playlistName, cascades }, { dispatch, getState, rejectWithValue }) => {
    const trimmedPlaylistId = typeof playlistId === 'string' ? playlistId.trim() : '';
    if (!trimmedPlaylistId) {
      return rejectWithValue('Playlist ID is required.');
    }

    const playlistLabel = playlistName ? `"${playlistName}"` : 'the playlist';
    const defaults = selectPlaylistCascadeDefaults(getState());
    const shouldFollow = cascades?.playlist ?? true;
    const shouldCascadeTracks = cascades?.tracks ?? defaults.save.tracks;
    const shouldCascadeRecordings = cascades?.recordings ?? defaults.save.recordings;

    if (shouldFollow) {
      try {
        await userService.followPlaylist(trimmedPlaylistId);
        message.success(`Saved ${playlistLabel} to Your Library`);
        void dispatch(fetchMyPlaylists());
      } catch (error) {
        const fallbackMessage = 'Failed to save playlist.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        notifyError(errorMessage);
        return rejectWithValue(errorMessage);
      }
    }

    let cascadedTrackCount = 0;
    let cascadedRecordingCount = 0;
    let totalItems = 0;

    if (shouldCascadeTracks || shouldCascadeRecordings) {
      try {
        const targets = await collectPlaylistCascadeTargets(trimmedPlaylistId, getState());
        totalItems = targets.totalItems;

        if (shouldCascadeTracks && targets.trackIds.length > 0) {
          cascadedTrackCount = await cascadeSaveTracks(targets.trackIds);
          if (cascadedTrackCount > 0) {
            const noun = cascadedTrackCount === 1 ? 'track' : 'tracks';
            message.success(`Also saved ${cascadedTrackCount} ${noun} from ${playlistLabel}`);
          }
        }

        if (shouldCascadeRecordings && targets.recordingIds.length > 0) {
          cascadedRecordingCount = await cascadeSaveRecordingIds(targets.recordingIds);
          if (cascadedRecordingCount > 0) {
            const noun = cascadedRecordingCount === 1 ? 'recording' : 'recordings';
            message.success(`Also saved ${cascadedRecordingCount} ${noun} from ${playlistLabel}`);
          }
          void dispatch(fetchMyRecordings());
        }
      } catch (error) {
        const fallbackMessage =
          'Saved the playlist, but we could not cascade its tracks or recordings.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        message.warning(errorMessage);
      }
    }

    return { cascadedTrackCount, cascadedRecordingCount, totalItems };
  }
);

export const removePlaylistWithCascade = createAsyncThunk<
  { cascadedTrackCount: number; cascadedRecordingCount: number; totalItems: number },
  PlaylistCascadePayload,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/removePlaylistWithCascade',
  async ({ playlistId, playlistName, cascades }, { dispatch, getState, rejectWithValue }) => {
    const trimmedPlaylistId = typeof playlistId === 'string' ? playlistId.trim() : '';
    if (!trimmedPlaylistId) {
      return rejectWithValue('Playlist ID is required.');
    }

    const playlistLabel = playlistName ? `"${playlistName}"` : 'the playlist';
    const defaults = selectPlaylistCascadeDefaults(getState());
    const shouldUnfollow = cascades?.playlist ?? true;
    const shouldCascadeTracks = cascades?.tracks ?? defaults.remove.tracks;
    const shouldCascadeRecordings = cascades?.recordings ?? defaults.remove.recordings;

    if (shouldUnfollow) {
      try {
        await userService.unfollowPlaylist(trimmedPlaylistId);
        message.success(`Removed ${playlistLabel} from Your Library`);
        void dispatch(fetchMyPlaylists());
      } catch (error) {
        const fallbackMessage = 'Failed to remove playlist.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        notifyError(errorMessage);
        return rejectWithValue(errorMessage);
      }
    }

    let cascadedTrackCount = 0;
    let cascadedRecordingCount = 0;
    let totalItems = 0;

    if (shouldCascadeTracks || shouldCascadeRecordings) {
      try {
        const targets = await collectPlaylistCascadeTargets(trimmedPlaylistId, getState());
        totalItems = targets.totalItems;

        if (shouldCascadeTracks && targets.trackIds.length > 0) {
          cascadedTrackCount = await cascadeDeleteTracks(targets.trackIds);
          if (cascadedTrackCount > 0) {
            const noun = cascadedTrackCount === 1 ? 'track' : 'tracks';
            message.success(`Also removed ${cascadedTrackCount} ${noun} from ${playlistLabel}`);
          }
        }

        if (shouldCascadeRecordings && targets.recordingIds.length > 0) {
          cascadedRecordingCount = await cascadeDeleteRecordingIds(targets.recordingIds);
          if (cascadedRecordingCount > 0) {
            const noun = cascadedRecordingCount === 1 ? 'recording' : 'recordings';
            message.success(`Also removed ${cascadedRecordingCount} ${noun} from ${playlistLabel}`);
          }
          void dispatch(fetchMyRecordings());
        }
      } catch (error) {
        const fallbackMessage =
          'Removed the playlist, but we could not cascade its tracks or recordings.';
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        message.warning(errorMessage);
      }
    }

    return { cascadedTrackCount, cascadedRecordingCount, totalItems };
  }
);

export const runSyncAlbumTracks = createAsyncThunk<
  SavedAlbumTracksSyncResult,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/runSyncAlbumTracks',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.auth.user?.id?.trim();

    if (!userId) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Please log in to run the sync.';
      void logAppEvent({
        eventName: 'runSyncAlbumRecordingsAndWorks_missing_user',
        level: 'warn',
        message: errorMessage,
      });
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const tokenCandidates: Array<string | undefined> = [
      state.auth.token,
      getFromLocalStorageWithExpiry('access_token') as string | undefined,
      getFromLocalStorageWithExpiry('public_access_token') as string | undefined
    ];

    const accessToken = tokenCandidates.find(
      (token) => typeof token === 'string' && token.trim().length > 0
    );

    if (!accessToken) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Spotify access token missing. Please log in and try again.';
      void logAppEvent({
        eventName: 'runSyncAlbumRecordingsAndWorks_missing_access_token',
        level: 'warn',
        userId,
        message: errorMessage,
      });
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    try {
      const result = await librarySyncSettingsService.runSavedAlbumTracksSync({
        spotifyUserId: userId,
        accessToken: accessToken.trim()
      });

      const trackNoun = result.saved === 1 ? 'track' : 'tracks';
      const albumNoun = result.totalAlbums === 1 ? 'album' : 'albums';
      message.success(
        `Sync complete: saved ${result.saved} ${trackNoun} from ${result.totalAlbums} ${albumNoun}.`
      );

      try {
        await dispatch(loadLibrarySyncSettings()).unwrap();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to refresh library sync settings after sync', error);
        }
      }

      return result;
    } catch (error) {
      const fallbackMessage = 'Failed to sync saved album tracks.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] runSyncAlbumTracks failed', error);
      }
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.yourLibrary.librarySyncSettings.isSyncingAlbumTracks;
    }
  }
);

export const runSyncAlbumRecordings = createAsyncThunk<
  SavedAlbumRecordingsSyncResult,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/runSyncAlbumRecordings',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.auth.user?.id?.trim();

    if (!userId) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Please log in to run the sync.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const tokenCandidates: Array<string | undefined> = [
      state.auth.token,
      getFromLocalStorageWithExpiry('access_token') as string | undefined,
      getFromLocalStorageWithExpiry('public_access_token') as string | undefined
    ];

    const accessToken = tokenCandidates.find(
      (token) => typeof token === 'string' && token.trim().length > 0
    );

    if (!accessToken) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Spotify access token missing. Please log in and try again.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    try {
      const result = await librarySyncSettingsService.runSavedAlbumRecordingsSync({
        spotifyUserId: userId,
        accessToken: accessToken.trim()
      });

      const recordingNoun = result.saved === 1 ? 'recording' : 'recordings';
      const albumNoun = result.totalAlbums === 1 ? 'album' : 'albums';
      message.success(
        `Sync complete: saved ${result.saved} ${recordingNoun} from ${result.totalAlbums} ${albumNoun}.`
      );

      try {
        await dispatch(loadLibrarySyncSettings()).unwrap();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to refresh library sync settings after recordings sync', error);
        }
      }

      return result;
    } catch (error) {
      const fallbackMessage = 'Failed to sync saved album recordings.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] runSyncAlbumRecordings failed', error);
      }
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.yourLibrary.librarySyncSettings.isSyncingAlbumRecordings;
    }
  }
);

export const runSyncRecordingWorks = createAsyncThunk<
  RecordingWorksSyncResult,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/runSyncRecordingWorks',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.auth.user?.id?.trim();

    if (!userId) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Please log in to run the sync.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const tokenCandidates: Array<string | undefined> = [
      state.auth.token,
      getFromLocalStorageWithExpiry('access_token') as string | undefined,
      getFromLocalStorageWithExpiry('public_access_token') as string | undefined
    ];

    const accessToken = tokenCandidates.find(
      (token) => typeof token === 'string' && token.trim().length > 0
    );

    if (!accessToken) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Spotify access token missing. Please log in and try again.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    try {
      const result = await librarySyncSettingsService.runRecordingWorksSync({
        spotifyUserId: userId,
        accessToken: accessToken.trim()
      });

      const recordingNoun = result.totalResolved === 1 ? 'recording' : 'recordings';
      const workNoun = result.worksEnsured === 1 ? 'work' : 'works';
      message.success(
        `Sync complete: ensured ${result.worksEnsured} ${workNoun} across ${result.totalResolved} ${recordingNoun}.`
      );

      try {
        await dispatch(loadLibrarySyncSettings()).unwrap();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[yourLibrary] Failed to refresh library sync settings after recording works sync', error);
        }
      }

      return result;
    } catch (error) {
      const fallbackMessage = 'Failed to sync recording works.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] runSyncRecordingWorks failed', error);
      }
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.yourLibrary.librarySyncSettings.isSyncingRecordingWorks;
    }
  }
);

export const runSyncAlbumRecordingsAndWorks = createAsyncThunk<
  RecordingsAndWorksSyncJobStatus,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  'yourLibrary/runSyncAlbumRecordingsAndWorks',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.auth.user?.id?.trim();

    if (!userId) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Please log in to run the sync.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const tokenCandidates: Array<string | undefined> = [
      state.auth.token,
      getFromLocalStorageWithExpiry('access_token') as string | undefined,
      getFromLocalStorageWithExpiry('public_access_token') as string | undefined
    ];

    const accessToken = tokenCandidates.find(
      (token) => typeof token === 'string' && token.trim().length > 0
    );

    if (!accessToken) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Spotify access token missing. Please log in and try again.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    const runAlbumRecordings = state.yourLibrary.importSyncSelections.syncRecordings;
    const runTrackRecordings = state.yourLibrary.importSyncSelections.syncTrackRecordings;

    if (!runAlbumRecordings && !runTrackRecordings) {
      const errorMessage = 'Select at least one recordings sync option.';
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }

    try {
      const job = await librarySyncSettingsService.startRecordingsAndWorksSync({
        spotifyUserId: userId,
        accessToken: accessToken.trim(),
        runAlbumRecordings,
        runTrackRecordings
      });
      return job;
    } catch (error) {
      const fallbackMessage = 'Failed to start recordings and works sync.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] runSyncAlbumRecordingsAndWorks failed', error);
      }
      void logAppEvent({
        eventName: 'runSyncAlbumRecordingsAndWorks_failed',
        level: 'error',
        userId,
        message: errorMessage,
        stack: error instanceof Error ? error.stack ?? null : null,
        context: {
          spotifyUserId: userId,
          hasAccessToken: Boolean(accessToken),
          phase: 'startRecordingsAndWorksSync',
          runAlbumRecordings,
          runTrackRecordings
        },
      });
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const jobId = state.yourLibrary.librarySyncSettings.recordingsAndWorksJobId;
      return (
        !state.yourLibrary.librarySyncSettings.isSyncingAlbumRecordingsAndWorks &&
        !jobId
      );
    }
  }
);

export const fetchRecordingsAndWorksJobStatus = createAsyncThunk<
  RecordingsAndWorksSyncJobStatus,
  { jobId: string },
  { dispatch: AppDispatch; state: RootState; rejectValue: { message: string; reset?: boolean } }
>(
  'yourLibrary/fetchRecordingsAndWorksJobStatus',
  async ({ jobId }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.auth.user?.id?.trim();

    if (!userId) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Please log in to resume the sync.';
      notifyError(errorMessage);
      return rejectWithValue({ message: errorMessage, reset: true });
    }

    const tokenCandidates: Array<string | undefined> = [
      state.auth.token,
      getFromLocalStorageWithExpiry('access_token') as string | undefined,
      getFromLocalStorageWithExpiry('public_access_token') as string | undefined
    ];

    const accessToken = tokenCandidates.find(
      (token) => typeof token === 'string' && token.trim().length > 0
    );

    if (!accessToken) {
      dispatch(uiActions.openLoginTooltip());
      const errorMessage = 'Spotify access token missing. Please log in and try again.';
      notifyError(errorMessage);
      return rejectWithValue({ message: errorMessage, reset: true });
    }

    try {
      const job = await librarySyncSettingsService.getRecordingsAndWorksSyncJobStatus({
        jobId,
        spotifyUserId: userId,
        accessToken: accessToken.trim()
      });

      const previousJob = state.yourLibrary.librarySyncSettings.recordingsAndWorksJob;
      const wasComplete = previousJob?.jobId === jobId && isRecordingsAndWorksJobComplete(previousJob);
      const isComplete = isRecordingsAndWorksJobComplete(job);

      if (!wasComplete && isComplete) {
        if (job.status === 'succeeded') {
          const recordingsSaved = job.recordings?.saved ?? 0;
          const albumsProcessed = job.recordings?.totalAlbums ?? 0;
          const worksEnsured = job.works?.worksEnsured ?? 0;
          message.success(
            `Sync complete: saved ${recordingsSaved} recordings from ${albumsProcessed} albums; ensured ${worksEnsured} works.`
          );
        } else {
          const fallbackMessage = 'Failed to sync recordings and works.';
          const errorMessage = job.error?.trim() || fallbackMessage;
          notifyError(errorMessage);
        }

        try {
          await dispatch(loadLibrarySyncSettings()).unwrap();
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[yourLibrary] Failed to refresh library sync settings after recordings+works sync',
              error
            );
          }
        }
      }

      return job;
    } catch (error: any) {
      const status = error?.response?.status;
      const fallbackMessage = 'Failed to fetch recordings and works sync status.';
      const errorMessage = extractErrorMessage(error, fallbackMessage);
      if (process.env.NODE_ENV === 'development') {
        console.error('[yourLibrary] fetchRecordingsAndWorksJobStatus failed', error);
      }
      const reset = status === 404;
      if (reset) {
        notifyError('Recordings/works sync job is no longer available.');
      }
      return rejectWithValue({ message: errorMessage, reset });
    }
  }
);

export const hydrateRecordingsAndWorksAfterSync = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('yourLibrary/hydrateRecordingsAndWorksAfterSync', async (_, { dispatch }) => {
  try {
    await dispatch(fetchMyRecordings()).unwrap();
  } catch {
    // best-effort
  }
  try {
    await dispatch(fetchMyWorks()).unwrap();
  } catch {
    // best-effort
  }
});

const yourLibrarySlice = createSlice({
  name: 'yourLibrary',
  initialState,
  reducers: {
    setFilter(state, action: PayloadAction<{ filter: string }>) {
      const normalized = normalizeLibraryFilter(action.payload.filter);
      if (state.filter !== normalized) {
        state.filter = normalized;
      }
    },
    setSearch(state, action: PayloadAction<{ search: string }>) {
      state.search = action.payload.search;
    },
    setView(state, action: PayloadAction<{ view: YourLibraryState['view'] }>) {
      state.view = action.payload.view;
    },
    setOrderBy(state, action: PayloadAction<{ orderBy: YourLibraryState['orderBy'] }>) {
      state.orderBy = action.payload.orderBy;
    },
    setComposerSortOrder(
      state,
      action: PayloadAction<{ composerSortOrder: YourLibraryState['composerSortOrder'] }>
    ) {
      state.composerSortOrder = action.payload.composerSortOrder;
    },
    setImportSyncSelections(
      state,
      action: PayloadAction<{ importLibrary?: boolean; syncRecordings?: boolean; syncTrackRecordings?: boolean }>
    ) {
      const { importLibrary, syncRecordings, syncTrackRecordings } = action.payload;
      if (typeof importLibrary === 'boolean') {
        state.importSyncSelections.importLibrary = importLibrary;
      }
      if (typeof syncRecordings === 'boolean') {
        state.importSyncSelections.syncRecordings = syncRecordings;
      }
      if (typeof syncTrackRecordings === 'boolean') {
        state.importSyncSelections.syncTrackRecordings = syncTrackRecordings;
      }
    },
    setRecordingsAndWorksJob(state, action: PayloadAction<RecordingsAndWorksSyncJobStatus | null>) {
      applyRecordingsAndWorksJobUpdate(state, action.payload ?? null);
    },
    mergeWorkMetadata(state, action: PayloadAction<LibraryWork>) {
      const incoming = action.payload;
      if (!incoming?.id) return;

      const index = state.myWorks.findIndex((work) => work.id === incoming.id);
      if (index === -1) {
        return;
      }

      const current = state.myWorks[index];
      const merged: LibraryWork = { ...current };

      const assign = <K extends keyof LibraryWork>(key: K) => {
        const value = incoming[key];
        if (value !== undefined && value !== merged[key]) {
          merged[key] = value as LibraryWork[K];
        }
      };

      assign('name');
      assign('composerName');
      assign('composerId');
      assign('movementCount');
      assign('imageUrl');
      assign('catalogueNumberNormalized');
      assign('catalogueNumber');
      assign('uri');
      assign('type');
      if (incoming.savedAt) {
        merged.savedAt = incoming.savedAt;
      }

      state.myWorks[index] = merged;
    },
    upsertMyPlaylists(state, action: PayloadAction<Playlist[]>) {
      upsertById(state.myPlaylists, action.payload);
    },
    upsertMyAlbums(state, action: PayloadAction<Album[]>) {
      upsertById(state.myAlbums, action.payload);
    },
    upsertMyArtists(state, action: PayloadAction<Artist[]>) {
      upsertById(state.myArtists, action.payload);
    },
    upsertMyWorks(state, action: PayloadAction<LibraryWork[]>) {
      upsertById(state.myWorks, action.payload);
    },
    upsertMyRecordings(state, action: PayloadAction<LibraryRecording[]>) {
      upsertById(state.myRecordings, action.payload);
    },
    setRecordingsStatus(
      state,
      action: PayloadAction<YourLibraryState['recordingsStatus']>
    ) {
      state.recordingsStatus = action.payload;
    },
    setAlbumsPageCursor(state, action: PayloadAction<string | null | undefined>) {
      const raw = action.payload;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        state.albumsPageCursor = trimmed.length > 0 ? trimmed : null;
        return;
      }
      state.albumsPageCursor = null;
    },
    setAlbumsPageSessionId(state, action: PayloadAction<string | null | undefined>) {
      const raw = action.payload;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        state.albumsPageSessionId = trimmed.length > 0 ? trimmed : null;
        return;
      }
      state.albumsPageSessionId = null;
    },
    setMyComposers(state, action: PayloadAction<LibraryComposer[]>) {
      state.myComposers = action.payload;
      markFetched(state, 'composers');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLibrarySyncSettings.pending, (state) => {
        state.librarySyncSettings.status = 'loading';
        state.librarySyncSettings.error = null;
        state.librarySyncSettings.pendingUpdate = null;
        state.librarySyncSettings.updateStatus = 'idle';
      })
      .addCase(loadLibrarySyncSettings.fulfilled, (state, action) => {
        state.librarySyncSettings.status = 'ready';
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(action.payload);
        state.librarySyncSettings.error = null;
        state.librarySyncSettings.updateStatus = 'idle';
        state.librarySyncSettings.pendingUpdate = null;
        state.librarySyncSettings.lastFetchedAt = Date.now();
      })
      .addCase(loadLibrarySyncSettings.rejected, (state, action) => {
        state.librarySyncSettings.status = 'error';
        state.librarySyncSettings.error =
          (typeof action.payload === 'string' && action.payload) ||
          action.error?.message ||
          'Failed to load library sync settings.';
        state.librarySyncSettings.updateStatus = 'idle';
        state.librarySyncSettings.pendingUpdate = null;
      });

    builder
      .addCase(updateLibrarySyncAutomation.pending, (state, action) => {
        const { ruleId, enabled } = action.meta.arg;
        const currentValue = state.librarySyncSettings.value;
        state.librarySyncSettings.updateStatus = 'pending';
        state.librarySyncSettings.error = null;
        state.librarySyncSettings.pendingUpdate = {
          ruleId,
          previousValue: cloneLibrarySyncSettingsValue(currentValue),
        };

        const nextValue = cloneLibrarySyncSettingsValue(currentValue);
        const existingAutomation = nextValue.automations[ruleId];
        const timestamp = new Date().toISOString();
        nextValue.automations[ruleId] = {
          ...existingAutomation,
          enabled,
          updatedAt: timestamp,
        };
        state.librarySyncSettings.value = nextValue;
      })
      .addCase(updateLibrarySyncAutomation.fulfilled, (state, action) => {
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(action.payload);
        state.librarySyncSettings.updateStatus = 'idle';
        state.librarySyncSettings.pendingUpdate = null;
        state.librarySyncSettings.error = null;
        state.librarySyncSettings.status = 'ready';
        state.librarySyncSettings.lastFetchedAt = Date.now();
      })
      .addCase(updateLibrarySyncAutomation.rejected, (state, action) => {
        const pendingUpdate = state.librarySyncSettings.pendingUpdate;
        if (pendingUpdate?.previousValue) {
          state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(pendingUpdate.previousValue);
        }
        state.librarySyncSettings.updateStatus = 'idle';
        state.librarySyncSettings.pendingUpdate = null;
        state.librarySyncSettings.status = 'ready';
        state.librarySyncSettings.error =
          (typeof action.payload === 'string' && action.payload) ||
          action.error?.message ||
          'Failed to update library sync settings.';
      });

    builder
      .addCase(runSyncAlbumTracks.pending, (state) => {
        state.librarySyncSettings.isSyncingAlbumTracks = true;
        state.librarySyncSettings.error = null;
      })
      .addCase(runSyncAlbumTracks.fulfilled, (state) => {
        state.librarySyncSettings.isSyncingAlbumTracks = false;
        state.librarySyncSettings.error = null;
        if (state.librarySyncSettings.status === 'idle') {
          state.librarySyncSettings.status = 'ready';
        }
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        state.librarySyncSettings.value.oneOffs.syncAlbumTracks.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncAlbumTracks.lastStatus = 'success';
        state.librarySyncSettings.lastFetchedAt = Date.now();
      })
      .addCase(runSyncAlbumTracks.rejected, (state, action) => {
        state.librarySyncSettings.isSyncingAlbumTracks = false;
        const errorMessage =
          (typeof action.payload === 'string' && action.payload) ||
          action.error?.message ||
          'Failed to sync saved album tracks.';
        state.librarySyncSettings.error = errorMessage;
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        state.librarySyncSettings.value.oneOffs.syncAlbumTracks.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncAlbumTracks.lastStatus = 'failed';
      });

    builder
      .addCase(runSyncAlbumRecordings.pending, (state) => {
        state.librarySyncSettings.isSyncingAlbumRecordings = true;
        state.librarySyncSettings.error = null;
      })
      .addCase(runSyncAlbumRecordings.fulfilled, (state) => {
        state.librarySyncSettings.isSyncingAlbumRecordings = false;
        state.librarySyncSettings.error = null;
        if (state.librarySyncSettings.status === 'idle') {
          state.librarySyncSettings.status = 'ready';
        }
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        state.librarySyncSettings.value.oneOffs.syncAlbumRecordings.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncAlbumRecordings.lastStatus = 'success';
        state.librarySyncSettings.lastFetchedAt = Date.now();
      })
      .addCase(runSyncAlbumRecordings.rejected, (state, action) => {
        state.librarySyncSettings.isSyncingAlbumRecordings = false;
        const errorMessage =
          (typeof action.payload === 'string' && action.payload) ||
          action.error?.message ||
          'Failed to sync saved album recordings.';
        state.librarySyncSettings.error = errorMessage;
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        state.librarySyncSettings.value.oneOffs.syncAlbumRecordings.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncAlbumRecordings.lastStatus = 'failed';
      });

    builder
      .addCase(runSyncRecordingWorks.pending, (state) => {
        state.librarySyncSettings.isSyncingRecordingWorks = true;
        state.librarySyncSettings.error = null;
      })
      .addCase(runSyncRecordingWorks.fulfilled, (state) => {
        state.librarySyncSettings.isSyncingRecordingWorks = false;
        state.librarySyncSettings.error = null;
        if (state.librarySyncSettings.status === 'idle') {
          state.librarySyncSettings.status = 'ready';
        }
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        state.librarySyncSettings.value.oneOffs.syncRecordingWorks.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncRecordingWorks.lastStatus = 'success';
        state.librarySyncSettings.lastFetchedAt = Date.now();
      })
      .addCase(runSyncRecordingWorks.rejected, (state, action) => {
        state.librarySyncSettings.isSyncingRecordingWorks = false;
        const errorMessage =
          (typeof action.payload === 'string' && action.payload) ||
          action.error?.message ||
          'Failed to sync recording works.';
        state.librarySyncSettings.error = errorMessage;
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        state.librarySyncSettings.value.oneOffs.syncRecordingWorks.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncRecordingWorks.lastStatus = 'failed';
      });

    builder
      .addCase(runSyncAlbumRecordingsAndWorks.pending, (state) => {
        state.librarySyncSettings.error = null;
        state.librarySyncSettings.recordingsAndWorksJob = null;
        state.librarySyncSettings.recordingsAndWorksJobId = null;
        state.librarySyncSettings.isSyncingAlbumRecordingsAndWorks = true;
        state.librarySyncSettings.isSyncingTrackRecordings = true;
        state.lastRecordingsAndWorksSyncStartedAt = Date.now();
      })
      .addCase(runSyncAlbumRecordingsAndWorks.fulfilled, (state, action) => {
        applyRecordingsAndWorksJobUpdate(state, action.payload);
        if (state.librarySyncSettings.status === 'idle') {
          state.librarySyncSettings.status = 'ready';
        }
      })
      .addCase(runSyncAlbumRecordingsAndWorks.rejected, (state, action) => {
        state.librarySyncSettings.isSyncingAlbumRecordingsAndWorks = false;
        state.librarySyncSettings.isSyncingTrackRecordings = false;
        state.librarySyncSettings.recordingsAndWorksJob = null;
        state.librarySyncSettings.recordingsAndWorksJobId = null;
        const errorMessage =
          (typeof action.payload === 'string' && action.payload) ||
          action.error?.message ||
          'Failed to sync recordings and works.';
        state.librarySyncSettings.error = errorMessage;
        state.librarySyncSettings.value = cloneLibrarySyncSettingsValue(
          state.librarySyncSettings.value
        );
        const nowIso = new Date().toISOString();
        state.librarySyncSettings.value.oneOffs.syncAlbumRecordings.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncRecordingWorks.lastRunAt = nowIso;
        state.librarySyncSettings.value.oneOffs.syncAlbumRecordings.lastStatus = 'failed';
        state.librarySyncSettings.value.oneOffs.syncRecordingWorks.lastStatus = 'failed';
        if (
          'syncTrackRecordings' in state.librarySyncSettings.value.oneOffs &&
          state.importSyncSelections.syncTrackRecordings
        ) {
          state.librarySyncSettings.value.oneOffs.syncTrackRecordings.lastRunAt = nowIso;
          state.librarySyncSettings.value.oneOffs.syncTrackRecordings.lastStatus = 'failed';
        }
      })
      .addCase(fetchRecordingsAndWorksJobStatus.pending, (state) => {
        state.librarySyncSettings.error = null;
      })
      .addCase(fetchRecordingsAndWorksJobStatus.fulfilled, (state, action) => {
        applyRecordingsAndWorksJobUpdate(state, action.payload);
        if (state.librarySyncSettings.status === 'idle') {
          state.librarySyncSettings.status = 'ready';
        }
      })
      .addCase(fetchRecordingsAndWorksJobStatus.rejected, (state, action) => {
        const payload = action.payload;
        const errorMessage =
          (payload && payload.message) ||
          action.error?.message ||
          'Failed to fetch recordings and works sync status.';
        state.librarySyncSettings.error = errorMessage;
        if (payload?.reset) {
          applyRecordingsAndWorksJobUpdate(state, null);
          state.librarySyncSettings.isSyncingAlbumRecordingsAndWorks = false;
        }
      });

    builder
      .addCase(fetchMyPlaylists.pending, (state) => {
        state.playlistsFetchInFlight = true;
        // Only surface loading when we don't already have a cache to show.
        if (
          state.playlistsStatus === 'idle' ||
          state.playlistsStatus === 'failed' ||
          state.myPlaylists.length === 0
        ) {
          state.playlistsStatus = 'loading';
        }
      })
      .addCase(fetchMyPlaylists.fulfilled, (state, action) => {
        markFetched(state, 'playlists');
        state.myPlaylists = action.payload;
        state.playlistsStatus = 'succeeded';
        state.playlistsFetchInFlight = false;
      })
      .addCase(fetchMyPlaylists.rejected, (state) => {
        state.playlistsStatus = 'failed';
        state.playlistsFetchInFlight = false;
      });
    builder
      .addCase(fetchMyAlbums.pending, (state) => {
        state.albumsFetchInFlight = true;
        state.albumsStatus = 'loading';
        state.albumsError = null;
        state.albumsSyncStatus = 'idle';
        state.albumsPageCursor = null;
        state.albumsPageSessionId = null;
      })
      .addCase(fetchMyAlbums.fulfilled, (state, action) => {
        state.albumsStatus = 'succeeded';
        state.albumsError = null;
        markFetched(state, 'albums');
        state.myAlbums = action.payload;
        state.albumsFetchInFlight = false;
      })
      .addCase(fetchMyAlbums.rejected, (state, action) => {
        state.albumsStatus = 'failed';
        state.albumsError = action.error?.message ?? 'Failed to load albums';
        state.albumsFetchInFlight = false;
      });
    builder
      .addCase(syncMyAlbums.pending, (state) => {
        state.albumsSyncStatus = 'pending';
        state.albumsError = null;
        state.albumsPageCursor = null;
        state.albumsPageSessionId = null;
      })
      .addCase(syncMyAlbums.fulfilled, (state, action) => {
        state.albumsSyncStatus = 'succeeded';
        state.albumsError = null;
        state.albumsPageCursor = action.payload.nextCursor ?? null;
        state.albumsPageSessionId = action.payload.sessionId ?? null;
      })
      .addCase(syncMyAlbums.rejected, (state, action) => {
        state.albumsSyncStatus = 'failed';
        state.albumsPageCursor = null;
        state.albumsPageSessionId = null;
        if (action.error?.message) {
          state.albumsError = action.error.message;
        }
    });
    builder
      .addCase(fetchMyArtists.pending, (state) => {
        state.artistsFetchInFlight = true;
        state.artistsStatus = 'loading';
      })
      .addCase(fetchMyArtists.fulfilled, (state, action) => {
        state.artistsStatus = 'succeeded';
        markFetched(state, 'artists');
        state.myArtists = action.payload;
        state.artistsFetchInFlight = false;
      })
      .addCase(fetchMyArtists.rejected, (state) => {
        state.artistsStatus = 'failed';
        state.artistsFetchInFlight = false;
      });
    builder.addCase(deriveMyComposers.fulfilled, (state, action) => {
      markFetched(state, 'composers');
      state.myComposers = action.payload.composers;
      state.composersSourceSignature = action.payload.signature;
      state.composersSourceUserId = action.payload.userId;
    });
    builder.addCase(fetchMyComposers.fulfilled, (state, action) => {
      markFetched(state, 'composers');
      state.myComposers = action.payload;
      state.composersSourceSignature = null;
      state.composersSourceUserId = null;
    });
    builder.addCase(fetchMyWorks.pending, (state) => {
      state.worksFetchInFlight = true;
      // Only surface loading when we don't already have a cache to show.
      if (
        state.worksStatus === 'idle' ||
        state.worksStatus === 'failed' ||
        state.myWorks.length === 0
      ) {
        state.worksStatus = 'loading';
      }
    });
    builder.addCase(removeWorkWithCascade.pending, (state, action) => {
      const shouldRemoveWork = action.meta.arg.removeWork !== false;
      const workId =
        typeof action.meta.arg.workId === 'string' ? action.meta.arg.workId.trim() : '';
      if (!shouldRemoveWork || !workId) return;
      state.myWorks = state.myWorks.filter(
        (work) => (typeof work?.id === 'string' ? work.id.trim() : '') !== workId
      );
    });
    builder.addCase(removeWorkWithCascade.rejected, (state, action) => {
      const shouldRemoveWork = action.meta.arg.removeWork !== false;
      const workId =
        typeof action.meta.arg.workId === 'string' ? action.meta.arg.workId.trim() : '';
      if (!shouldRemoveWork || !workId) return;
      const alreadyPresent = state.myWorks.some(
        (work) => (typeof work?.id === 'string' ? work.id.trim() : '') === workId
      );
      if (alreadyPresent) return;
      const workName =
        typeof action.meta.arg.workName === 'string' && action.meta.arg.workName.trim().length
          ? action.meta.arg.workName.trim()
          : workId;
      state.myWorks.unshift({
        id: workId,
        uri: `cmf:work:${workId}`,
        type: 'work',
        name: workName,
        composerName: '',
        savedAt: new Date().toISOString(),
      });
    });
    builder.addCase(fetchMyWorks.fulfilled, (state, action) => {
      markFetched(state, 'works');
      state.myWorks = action.payload;
      state.worksStatus = 'succeeded';
      state.worksFetchInFlight = false;
    });
    builder.addCase(fetchMyWorks.rejected, (state) => {
      state.worksStatus = 'failed';
      state.worksFetchInFlight = false;
    });
    builder.addCase(removeWorkWithCascade.fulfilled, (state, action) => {
      if (action.payload.removedWork) {
        const removedId = action.payload.workId;
        state.myWorks = state.myWorks.filter(
          (work) => (typeof work?.id === 'string' ? work.id.trim() : '') !== removedId
        );
      }
    });
    builder.addCase(fetchMyRecordings.pending, (state) => {
      state.recordingsFetchInFlight = true;
      // Only surface loading when we don't already have a cache to show.
      if (
        state.recordingsStatus === 'idle' ||
        state.recordingsStatus === 'failed' ||
        state.myRecordings.length === 0
      ) {
        state.recordingsStatus = 'loading';
      }
    });
    builder.addCase(fetchMyRecordings.fulfilled, (state, action) => {
      state.recordingsStatus = 'succeeded';
      markFetched(state, 'recordings');
      state.myRecordings = action.payload;
      state.recordingsFetchInFlight = false;
    });
    builder.addCase(fetchMyRecordings.rejected, (state) => {
      state.recordingsStatus = 'failed';
      state.recordingsFetchInFlight = false;
    });
    builder.addCase(importLibraryFromSpotify.pending, (state) => {
      state.importStatus = 'pending';
      state.importError = null;
      state.lastImportStartedAt = Date.now();
      state.lastImportRequiresAlbumSync = state.importSyncSelections.syncRecordings;
      state.lastImportRequiresTrackSync = state.importSyncSelections.syncTrackRecordings;
      state.lastImportCompletedAt = null;
    });
    builder.addCase(importLibraryFromSpotify.fulfilled, (state, action) => {
      const { artists, playlists, albums } = action.payload;
      if (Array.isArray(artists) && artists.length) {
        upsertById(state.myArtists, artists);
        markFetched(state, 'artists');
        state.artistsStatus = 'succeeded';
      }
      if (Array.isArray(playlists) && playlists.length) {
        upsertById(state.myPlaylists, playlists);
        markFetched(state, 'playlists');
        state.playlistsStatus = 'succeeded';
      }
      if (Array.isArray(albums) && albums.length) {
        upsertById(state.myAlbums, albums);
        markFetched(state, 'albums');
        state.albumsStatus = 'succeeded';
      }
      state.importStatus = 'succeeded';
      state.importError = null;
      state.lastImportCompletedAt = Date.now();
    });
    builder.addCase(importLibraryFromSpotify.rejected, (state, action) => {
      state.importStatus = 'failed';
      state.importError =
        typeof action.payload === 'string'
          ? action.payload
          : action.error?.message ?? 'Failed to import library from Spotify.';
      state.lastImportStartedAt = null;
      state.lastImportRequiresAlbumSync = null;
      state.lastImportRequiresTrackSync = null;
    });
  },
});

export const selectLibrarySyncSettingsState = (state: RootState) =>
  state.yourLibrary.librarySyncSettings;

const selectAutomationMap = (state: RootState) =>
  state.yourLibrary.librarySyncSettings.value.automations ?? {};

const resolveAutomationEnabled = (
  automations: ReturnType<typeof selectAutomationMap>,
  automationId: LibrarySyncAutomationId | undefined,
  fallback = false
): boolean => {
  if (!automationId) return fallback;
  const entry = automations[automationId];
  if (!entry) return fallback;
  const { enabled } = entry;
  return typeof enabled === 'boolean' ? enabled : fallback;
};

type AlbumCascadeScope = 'album' | 'recordings' | 'tracks';
type RecordingCascadeScope = 'recording' | 'tracks';
type PlaylistCascadeScope = 'playlist' | 'recordings' | 'tracks';
type WorkCascadeScope = 'work' | 'recordings';

type CascadeMode = 'save' | 'remove';

const buildCascadeDefaults = <Scope extends string>(
  overrides: Record<CascadeMode, Record<Scope, boolean>>
) => overrides;

export const selectAlbumCascadeDefaults = createSelector(
  [selectAutomationMap],
  (automations): Record<CascadeMode, Record<AlbumCascadeScope, boolean>> =>
    buildCascadeDefaults<AlbumCascadeScope>({
      save: {
        album: true,
        recordings: resolveAutomationEnabled(automations, 'albumSaveRecordings'),
        tracks: resolveAutomationEnabled(automations, 'albumSaveCascade'),
      },
      remove: {
        album: true,
        recordings: resolveAutomationEnabled(automations, 'albumUnsaveRecordings'),
        tracks: resolveAutomationEnabled(automations, 'albumUnsaveCascade'),
      },
    })
);

export const selectRecordingCascadeDefaults = createSelector(
  [selectAutomationMap],
  (automations): Record<CascadeMode, Record<RecordingCascadeScope, boolean>> =>
    buildCascadeDefaults<RecordingCascadeScope>({
      save: {
        recording: true,
        tracks: resolveAutomationEnabled(automations, 'recordingSaveTracks'),
      },
      remove: {
        recording: true,
        tracks: resolveAutomationEnabled(automations, 'recordingUnsaveTracks'),
      },
    })
);

export const selectPlaylistCascadeDefaults = createSelector(
  [selectAutomationMap],
  (automations): Record<CascadeMode, Record<PlaylistCascadeScope, boolean>> =>
    buildCascadeDefaults<PlaylistCascadeScope>({
      save: {
        playlist: true,
        recordings: false,
        tracks: false,
      },
      remove: {
        playlist: true,
        recordings: false,
        tracks: false,
      },
    })
);

export const selectWorkCascadeDefaults = createSelector(
  [selectAutomationMap],
  (automations): Record<CascadeMode, Record<WorkCascadeScope, boolean>> =>
    buildCascadeDefaults<WorkCascadeScope>({
      save: {
        work: true,
        recordings: false,
      },
      remove: {
        work: true,
        recordings: resolveAutomationEnabled(automations, 'workUnsaveRecordings', true),
      },
    })
);

export const getLibraryItems = createSelector(
  [
    (state: RootState) => state.auth.user,
    (state: RootState) => state.yourLibrary.filter,
    (state: RootState) => state.yourLibrary.composerSortOrder,
    (state: RootState) => state.yourLibrary.myAlbums,
    (state: RootState) => state.yourLibrary.myArtists,
    (state: RootState) => state.yourLibrary.myPlaylists,
    (state: RootState) => state.yourLibrary.myComposers,
    (state: RootState) => state.yourLibrary.myWorks,
    (state: RootState) => state.yourLibrary.myRecordings,
  ],
  (
    user,
    filter,
    composerSortOrder,
    myAlbums,
    myArtists,
    myPlaylists,
    myComposers,
    myWorks,
    myRecordings
  ) => {
    const effectiveFilter = normalizeLibraryFilter(filter);
    const mixBuilder =
      user && typeof user === 'object'
        ? ({
            id: MIX_BUILDER_PLAYLIST_ID,
            name: 'Mix Builder',
            snapshot_id: '',
            collaborative: false,
            public: false,
            description: 'Build custom mixes',
            href: '',
            type: 'playlist',
            tracks: { href: '', total: 0 },
            external_urls: { spotify: '' },
            followers: { href: '', total: 0 },
            uri: MIX_BUILDER_URI,
            images: [{ url: PLAYLIST_DEFAULT_IMAGE, width: 300, height: 300 }],
            owner: user!,
          } as Playlist)
        : null;
    const likedSongs =
      user && typeof user === 'object'
        ? ({
            id: 'liked-songs',
            name: 'Liked Songs',
            snapshot_id: '',
            collaborative: false,
            public: false,
            description: '',
            href: '',
            type: 'playlist',
            tracks: { href: '', total: 0 },
            external_urls: { spotify: '' },
            followers: { href: '', total: 0 },
            uri: `spotify:user:${user?.id}:collection`,
            images: [{ url: LIKED_SONGS_IMAGE, width: 300, height: 300 }],
            owner: user!,
          } as Playlist)
        : null;
    const isLikedSongs = (playlist: Playlist | null | undefined): boolean => {
      if (!playlist || !likedSongs) return false;
      return (
        playlist.id === likedSongs.id ||
        (Boolean(playlist.uri) && Boolean(likedSongs.uri) && playlist.uri === likedSongs.uri)
      );
    };
    const isMixBuilder = (playlist: Playlist | null | undefined): boolean => {
      if (!playlist || !mixBuilder) return false;
      return playlist.id === mixBuilder.id;
    };

    if (effectiveFilter === 'ALBUMS') return myAlbums;
    if (effectiveFilter === 'ARTISTS') return myArtists;
    if (effectiveFilter === 'PLAYLISTS') {
      const playlistPool = myPlaylists.filter(
        (playlist) => !isLikedSongs(playlist) && !isMixBuilder(playlist)
      );

      const orderedPlaylists: Playlist[] = [];
      if (mixBuilder) orderedPlaylists.push(mixBuilder);
      if (likedSongs) orderedPlaylists.push(likedSongs);
      orderedPlaylists.push(...playlistPool);

      return orderedPlaylists;
    }
    if (effectiveFilter === 'COMPOSERS') {
      if (composerSortOrder !== 'relevance') {
        return myComposers;
      }

      const workCounts = new Map<string, number>();
      for (const work of myWorks) {
        const composerId = work?.composerId;
        if (!composerId) continue;
        workCounts.set(composerId, (workCounts.get(composerId) ?? 0) + 1);
      }

      const localeCompare = (valueA: string, valueB: string): number => {
        return valueA.localeCompare(valueB, undefined, { sensitivity: 'base' });
      };

      const sorted = [...myComposers].sort((a, b) => {
        const countA = workCounts.get(a?.id ?? '') ?? 0;
        const countB = workCounts.get(b?.id ?? '') ?? 0;
        if (countA !== countB) {
          return countB - countA;
        }
        const nameA = typeof a?.name === 'string' ? a.name : '';
        const nameB = typeof b?.name === 'string' ? b.name : '';
        const fallback = localeCompare(nameA, nameB);
        if (fallback !== 0) {
          return fallback;
        }
        return localeCompare(a?.id ?? '', b?.id ?? '');
      });

      return sorted;
    }
    if (effectiveFilter === 'WORKS') return myWorks;
    if (effectiveFilter === 'RECORDINGS') return myRecordings;

    if (!user) return [];
    if (
      !myAlbums.length &&
      !myArtists.length &&
      !myPlaylists.length &&
      !myComposers.length &&
      !myWorks.length &&
      !myRecordings.length
    )
      return [];

    const playlistPool =
      likedSongs || mixBuilder
        ? myPlaylists.filter((playlist) => !isLikedSongs(playlist) && !isMixBuilder(playlist))
        : myPlaylists;

    const sections: Array<LibraryItem | LibraryItem[] | null> = [
      mixBuilder,
      likedSongs,
      playlistPool.slice(0, 3),
      myAlbums.slice(0, 2),
      playlistPool.slice(3, 6),
      myArtists.slice(0, 1),
      myAlbums.slice(2, 5),
      myArtists.slice(1, 2),
      playlistPool.slice(6, 10),
      myAlbums.slice(5, 9),
      myArtists.slice(2, 6),
      playlistPool.slice(10, 15),
      myAlbums.slice(9, 13),
      myArtists.slice(6, 10),
      playlistPool.slice(15, 20),
      myAlbums.slice(13, 17),
      myArtists.slice(10, 14),
      playlistPool.slice(20, 25),
      myAlbums.slice(17, 21),
      myArtists.slice(14, 18),
      playlistPool.slice(25, 30),
      myAlbums.slice(21, 25),
      myArtists.slice(18, 22),
      playlistPool.slice(30, 35),
      myAlbums.slice(25, 43),
      // all the rest
      playlistPool.slice(35),
      myArtists.slice(22),
      myAlbums.slice(43),
    ];

    const libraryItems: LibraryItem[] = sections.flatMap((entry) => {
      if (!entry) return [];
      return Array.isArray(entry) ? entry : [entry];
    });

    return libraryItems;
  }
);

export const getUserPlaylists = createSelector(
  [(state: RootState) => state.yourLibrary.myPlaylists, (state: RootState) => state.auth.user],
  (playlists, user) => {
    return playlists.filter((playlist) => playlist.owner?.id === user?.id);
  }
);

export const yourLibraryActions = {
  loadLibrarySyncSettings,
  updateLibrarySyncAutomation,
  saveRecordingsWithCascade,
  removeRecordingsWithCascade,
  saveRecordingWithCascade,
  removeRecordingWithCascade,
  removeWorkWithCascade,
  saveAlbumWithCascade,
  removeAlbumWithCascade,
  savePlaylistWithCascade,
  removePlaylistWithCascade,
  runSyncAlbumTracks,
  runSyncAlbumRecordings,
  runSyncRecordingWorks,
  runSyncAlbumRecordingsAndWorks,
  fetchRecordingsAndWorksJobStatus,
  hydrateRecordingsAndWorksAfterSync,
  importLibraryFromSpotify,
  fetchMyAlbums,
  syncMyAlbums,
  fetchMyArtists,
  deriveMyComposers,
  fetchMyPlaylists,
  fetchMyComposers,
  fetchMyWorks,
  fetchMyRecordings,
  persistLibraryCache,
  fetchLibraryCoreFromDb,
  ...yourLibrarySlice.actions,
};

export default yourLibrarySlice.reducer;
